import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  getBrokerConnections,
  getBrokers,
  connectBroker,
  refreshBrokerConnection,
  deleteBrokerConnection,
} from "../services/api.broker";
import {
  getStrategies,
  getCustomStrategies,
  getStrategyTemplates,
  getStrategyCatalog,
  getAccountStrategies,
  getAutoTradeStatus,
  getAutoTradeRuns,
  getExecutionOverview,
  getExecutionJobs,
} from "../services/api.strategies";
import { authUpdateMe } from "../services/api.auth";
import {
  getNotificationPreferences,
  sendTestEmailNotification,
  sendTestTelegramNotification,
  updateNotificationPreferences,
} from "../services/api.notifications";
import { patchAdoptExternalTrades } from "../services/api.trading";
import api from "../services/api";
import {
  Save,
  Settings as SettingsIcon,
  Shield,
  Key,
  PlugZap,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  Loader2,
  Activity,
  TrendingUp,
  AlertTriangle,
  Zap,
  Target,
  DollarSign,
  Percent,
  RefreshCw,
  Link,
  Unlink,
  Play,
  Pause,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Layers,
  Search,
  ClipboardList,
  Bell,
  Mail,
  Send,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import PlatformShell from "../components/PlatformShell";
import BacktestPanel from "../components/BacktestPanel";
import SettingsFlowNav from "../components/SettingsFlowNav";
import StrategyRuntimeFields from "../components/StrategyRuntimeFields";
import VisualStrategyBuilder from "../components/VisualStrategyBuilder";
import CodeEditor from "../components/CodeEditor";
import { useAuth } from "../context/AuthContext";
import { getApiErrorMessage } from "../lib/apiErrors";
import {
  formatDateTimeDisplay,
  getBrowserTimezone,
  resolveDisplayTimezone,
} from "../lib/datetime";
import {
  STRATEGY_ENGINES,
  buildRuntimeDefinition,
  getDefaultRuntimeParams,
  inferStrategyEngine,
  normalizeIndicatorsForApi,
} from "../lib/strategyRuntime";
import {
  findInstrumentFamilyConflict,
  getKnownInstrumentCatalog,
  instrumentOptionsFromSymbols,
} from "../lib/instruments";

const DEFAULT_RISK_SETTINGS = {
  stop_loss_enabled: true,
  stop_loss_type: "TICKS",
  stop_loss_value: 10,
  take_profit_enabled: true,
  take_profit_type: "TICKS",
  take_profit_value: 20,
  max_daily_drawdown: 500,
  max_daily_trades: 20,
  max_position_size: 5,
  risk_per_trade_percent: 1,
  topstep_bracket_mode: "unknown",
  // Maximum Position Loss
  enable_max_position_loss: false,
  max_position_loss_type: "currency",
  max_position_loss_value: 150,
  max_position_loss_action: "close_position",
  // Market Structure Trailing Stop
  enable_structure_profit_stop: false,
  structure_initial_stop_timeframe_minutes: 2,
  structure_stop_timeframe_minutes: 5,
  structure_stop_lookback_bars: 20,
  structure_stop_buffer_ticks: 8,
  structure_stop_min_profit_type: "points",
  structure_stop_min_profit_value: 25,
  structure_zone_detection_method: "swing_high_low",
  structure_stop_profit_only: true,
  enable_structure_initial_stop: false,
  enable_three_bar_reversal_exit: false,
  three_bar_reversal_exit_timeframe: 5,
  three_bar_reversal_exit_mode: "enhanced_only",
  three_bar_reversal_exit_action: "close_position",
  three_bar_reversal_exit_pnl_mode: "profit_only",
  three_bar_reversal_exit_profit_only: true,
  three_bar_reversal_exit_min_profit_value: "",
  three_bar_reversal_exit_confirm_on_closed_bar: true,
  // Profit Lock / Breakeven
  enable_profit_lock_stop: false,
  profit_lock_trigger_type: "points",
  profit_lock_trigger_value: 16,
  profit_lock_offset_points: 2,
  // Maximum Profit Giveback
  enable_profit_giveback_protection: false,
  profit_giveback_activation_type: "points",
  profit_giveback_activation_value: 20,
  profit_giveback_max_giveback_percent: 50,
  profit_giveback_action: "close_position",
};

const TRADE_MANAGEMENT_TIMEFRAMES = [
  { value: 1, label: "1 minute" },
  { value: 2, label: "2 minutes" },
  { value: 3, label: "3 minutes" },
  { value: 5, label: "5 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "60 minutes" },
];

const DEFAULT_USER_TIMEZONE = "America/New_York";

const THREE_BAR_REVERSAL_PNL_MODES = ["profit_only", "loss_only", "both"];

const normalizeThreeBarReversalPnlMode = (value) => {
  const normalized = String(value || "").toLowerCase();
  return THREE_BAR_REVERSAL_PNL_MODES.includes(normalized) ? normalized : "profit_only";
};

const resolveThreeBarReversalPnlMode = (data = {}) => normalizeThreeBarReversalPnlMode(
  data.three_bar_reversal_exit_pnl_mode
    || (data.three_bar_reversal_exit_profit_only === false ? "both" : "profit_only")
);

const buildTimezoneOptions = (...preferredTimezones) => {
  const curatedTimezones = [
    "America/New_York",
    "America/Toronto",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "UTC",
    "Europe/London",
    "Europe/Paris",
    "Asia/Dubai",
    "Asia/Singapore",
    "Asia/Tokyo",
    "Australia/Sydney",
  ];

  return [...new Set([...preferredTimezones.filter(Boolean), ...curatedTimezones])];
};

const mapApiRiskToUi = (data = {}) => ({
  stop_loss_enabled: data.stop_loss_enabled ?? true,
  stop_loss_type: data.stop_loss_percent ? "PERCENT" : "TICKS",
  stop_loss_value: data.stop_loss_percent || data.stop_loss_points || 10,
  take_profit_enabled: data.take_profit_enabled ?? true,
  take_profit_type: data.take_profit_percent ? "PERCENT" : "TICKS",
  take_profit_value: data.take_profit_percent || data.take_profit_points || 20,
  max_daily_drawdown: data.daily_drawdown_limit ?? 500,
  max_daily_trades: data.max_daily_trades ?? 20,
  max_position_size: data.max_position_size ?? 5,
  risk_per_trade_percent: data.risk_per_trade_percent ?? 1,
  topstep_bracket_mode: data.topstep_bracket_mode ?? "unknown",
  // Maximum Position Loss
  enable_max_position_loss: data.enable_max_position_loss ?? false,
  max_position_loss_type: data.max_position_loss_type ?? "currency",
  max_position_loss_value: data.max_position_loss_value ?? 150,
  max_position_loss_action: data.max_position_loss_action ?? "close_position",
  // Market Structure Trailing Stop
  enable_structure_profit_stop: data.enable_structure_profit_stop ?? false,
  structure_initial_stop_timeframe_minutes: data.structure_initial_stop_timeframe_minutes ?? 2,
  structure_stop_timeframe_minutes: data.structure_stop_timeframe_minutes ?? 5,
  structure_stop_lookback_bars: data.structure_stop_lookback_bars ?? 20,
  structure_stop_buffer_ticks: data.structure_stop_buffer_ticks ?? 8,
  structure_stop_min_profit_type: data.structure_stop_min_profit_type ?? "points",
  structure_stop_min_profit_value: data.structure_stop_min_profit_value ?? 25,
  structure_zone_detection_method: data.structure_zone_detection_method ?? "swing_high_low",
  structure_stop_profit_only: data.structure_stop_profit_only ?? true,
  enable_structure_initial_stop: data.enable_structure_initial_stop ?? false,
  enable_three_bar_reversal_exit: data.enable_three_bar_reversal_exit ?? data.enable_three_bar_reversal_take_profit ?? false,
  three_bar_reversal_exit_timeframe: data.three_bar_reversal_exit_timeframe ?? data.three_bar_reversal_timeframe_minutes ?? 5,
  three_bar_reversal_exit_mode: data.three_bar_reversal_exit_mode ?? "enhanced_only",
  three_bar_reversal_exit_action: data.three_bar_reversal_exit_action ?? "close_position",
  three_bar_reversal_exit_pnl_mode: resolveThreeBarReversalPnlMode(data),
  three_bar_reversal_exit_profit_only: resolveThreeBarReversalPnlMode(data) === "profit_only",
  three_bar_reversal_exit_min_profit_value: data.three_bar_reversal_exit_min_profit_value ?? "",
  three_bar_reversal_exit_confirm_on_closed_bar: data.three_bar_reversal_exit_confirm_on_closed_bar ?? true,
  // Profit Lock / Breakeven
  enable_profit_lock_stop: data.enable_profit_lock_stop ?? false,
  profit_lock_trigger_type: data.profit_lock_trigger_type ?? "points",
  profit_lock_trigger_value: data.profit_lock_trigger_value ?? 16,
  profit_lock_offset_points: data.profit_lock_offset_points ?? 2,
  // Maximum Profit Giveback
  enable_profit_giveback_protection: data.enable_profit_giveback_protection ?? false,
  profit_giveback_activation_type: data.profit_giveback_activation_type ?? "points",
  profit_giveback_activation_value: data.profit_giveback_activation_value ?? 20,
  profit_giveback_max_giveback_percent: data.profit_giveback_max_giveback_percent ?? 50,
  profit_giveback_action: data.profit_giveback_action ?? "close_position",
});

const mapUiRiskToApi = (riskSettings) => ({
  stop_loss_enabled: Boolean(riskSettings.stop_loss_enabled ?? true),
  stop_loss_percent: riskSettings.stop_loss_type === "PERCENT" ? Number(riskSettings.stop_loss_value || 0) : 0,
  stop_loss_points: riskSettings.stop_loss_type === "PERCENT" ? 0 : Number(riskSettings.stop_loss_value || 0),
  take_profit_enabled: Boolean(riskSettings.take_profit_enabled ?? true),
  take_profit_percent: riskSettings.take_profit_type === "PERCENT" ? Number(riskSettings.take_profit_value || 0) : 0,
  take_profit_points: riskSettings.take_profit_type === "PERCENT" ? 0 : Number(riskSettings.take_profit_value || 0),
  max_daily_trades: Number(riskSettings.max_daily_trades || 0),
  max_position_size: Number(riskSettings.max_position_size || 0),
  risk_per_trade_percent: Number(riskSettings.risk_per_trade_percent || 0),
  daily_drawdown_limit: Number(riskSettings.max_daily_drawdown || 0),
  topstep_bracket_mode: riskSettings.topstep_bracket_mode || "unknown",
  enabled: true,
  // Maximum Position Loss
  enable_max_position_loss: Boolean(riskSettings.enable_max_position_loss),
  max_position_loss_type: riskSettings.max_position_loss_type || "currency",
  max_position_loss_value: Number(riskSettings.max_position_loss_value || 150),
  max_position_loss_action: riskSettings.max_position_loss_action || "close_position",
  // Market Structure Trailing Stop
  enable_structure_profit_stop: Boolean(riskSettings.enable_structure_profit_stop),
  structure_initial_stop_timeframe_minutes: Number(riskSettings.structure_initial_stop_timeframe_minutes || 2),
  structure_stop_timeframe_minutes: Number(riskSettings.structure_stop_timeframe_minutes || 5),
  structure_stop_lookback_bars: Number(riskSettings.structure_stop_lookback_bars || 20),
  structure_stop_buffer_ticks: Number(riskSettings.structure_stop_buffer_ticks || 8),
  structure_stop_min_profit_type: riskSettings.structure_stop_min_profit_type || "points",
  structure_stop_min_profit_value: Number(riskSettings.structure_stop_min_profit_value || 25),
  structure_zone_detection_method: riskSettings.structure_zone_detection_method || "swing_high_low",
  structure_stop_profit_only: Boolean(riskSettings.structure_stop_profit_only ?? true),
  enable_structure_initial_stop: Boolean(riskSettings.enable_structure_initial_stop),
  enable_three_bar_reversal_exit: Boolean(riskSettings.enable_three_bar_reversal_exit),
  three_bar_reversal_exit_timeframe: Number(riskSettings.three_bar_reversal_exit_timeframe || 5),
  three_bar_reversal_exit_mode: riskSettings.three_bar_reversal_exit_mode || "enhanced_only",
  three_bar_reversal_exit_action: riskSettings.three_bar_reversal_exit_action || "close_position",
  three_bar_reversal_exit_pnl_mode: normalizeThreeBarReversalPnlMode(riskSettings.three_bar_reversal_exit_pnl_mode),
  three_bar_reversal_exit_profit_only: normalizeThreeBarReversalPnlMode(riskSettings.three_bar_reversal_exit_pnl_mode) === "profit_only",
  three_bar_reversal_exit_min_profit_value: riskSettings.three_bar_reversal_exit_min_profit_value === "" || riskSettings.three_bar_reversal_exit_min_profit_value === null
    ? null
    : Number(riskSettings.three_bar_reversal_exit_min_profit_value),
  three_bar_reversal_exit_confirm_on_closed_bar: Boolean(riskSettings.three_bar_reversal_exit_confirm_on_closed_bar ?? true),
  enable_three_bar_reversal_take_profit: Boolean(riskSettings.enable_three_bar_reversal_exit),
  three_bar_reversal_timeframe_minutes: Number(riskSettings.three_bar_reversal_exit_timeframe || 5),
  // Profit Lock / Breakeven
  enable_profit_lock_stop: Boolean(riskSettings.enable_profit_lock_stop),
  profit_lock_trigger_type: riskSettings.profit_lock_trigger_type || "points",
  profit_lock_trigger_value: Number(riskSettings.profit_lock_trigger_value || 16),
  profit_lock_offset_points: Number(riskSettings.profit_lock_offset_points ?? 2),
  // Maximum Profit Giveback
  enable_profit_giveback_protection: Boolean(riskSettings.enable_profit_giveback_protection),
  profit_giveback_activation_type: riskSettings.profit_giveback_activation_type || "points",
  profit_giveback_activation_value: Number(riskSettings.profit_giveback_activation_value || 20),
  profit_giveback_max_giveback_percent: Number(riskSettings.profit_giveback_max_giveback_percent || 50),
  profit_giveback_action: riskSettings.profit_giveback_action || "close_position",
});

const defaultAuditFilters = () => ({
  q: "",
  trading_account_id: "all",
  start: "",
  end: "",
});

const compactId = (value) => {
  if (!value) return "--";
  const text = String(value);
  return text.length > 18 ? `${text.slice(0, 8)}...${text.slice(-6)}` : text;
};

const formatAuditMoney = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "--";
  return `${amount >= 0 ? "+" : "-"}$${Math.abs(amount).toFixed(2)}`;
};

const createEmptyStrategyDraft = () => ({
  mode: "visual",
  name: "",
  description: "",
  language: "pinescript",
  code: "",
  strategy_type: "TREND_FOLLOWING",
  runtime_params: getDefaultRuntimeParams("TREND_FOLLOWING"),
  indicators: normalizeIndicatorsForApi(["RSI", "EMA", "SUPERTREND"]),
  entry_conditions: {},
  exit_conditions: {}
});

const normalizeAutoTradeState = (data = {}) => ({
  enabled: false,
  paper_mode: true,
  enabled_account_ids: [],
  signal_scan_enabled: false,
  signal_scan_symbol: "ES",
  last_scan_at: null,
  last_execution_at: null,
  last_error: null,
  ...data,
  enabled_account_ids: Array.isArray(data?.enabled_account_ids) ? data.enabled_account_ids : [],
});

const withFallbackResponse = (request, fallbackData) =>
  Promise.resolve()
    .then(request)
    .then((response) => response || { data: fallbackData })
    .catch(() => ({ data: fallbackData }));

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const browserTimezone = useMemo(
    () => getBrowserTimezone() || DEFAULT_USER_TIMEZONE,
    []
  );
  const timezoneOptions = useMemo(
    () => buildTimezoneOptions(user?.timezone, browserTimezone),
    [browserTimezone, user?.timezone]
  );

  // State
  const [activeTab, setActiveTab] = useState("accounts");
  const [loading, setLoading] = useState(true);
  const [userTimezone, setUserTimezone] = useState(user?.timezone || browserTimezone);
  const [discordWebhookUrl, setDiscordWebhookUrl] = useState(user?.preferences?.discord_webhook_url || "");
  const appTimeZone = resolveDisplayTimezone(userTimezone || user?.timezone || browserTimezone);
  const [savingUserPreferences, setSavingUserPreferences] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    email_enabled: user?.preferences?.alert_email_enabled ?? true,
    telegram_enabled: user?.preferences?.alert_telegram_enabled ?? false,
    telegram_chat_id: user?.preferences?.telegram_chat_id || "",
    telegram_bot_configured: false,
  });
  const [loadingNotificationPreferences, setLoadingNotificationPreferences] = useState(true);
  const [savingNotificationPreferences, setSavingNotificationPreferences] = useState(false);
  const [testingNotificationChannel, setTestingNotificationChannel] = useState("");
  const [showTelegramChatId, setShowTelegramChatId] = useState(false);
  
  // Trading Accounts
  const [accounts, setAccounts] = useState([]);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ name: "", broker_type: "topstepx", username: "", api_key: "" });
  const [showApiKey, setShowApiKey] = useState({});
  const [connectingAccount, setConnectingAccount] = useState(null);
  const [availableBrokers, setAvailableBrokers] = useState([]);
  const [refreshingToken, setRefreshingToken] = useState({});
  const [showTokens, setShowTokens] = useState({});

  const handleRefreshBroker = async (connectionId) => {
    setRefreshingToken((prev) => ({ ...prev, [connectionId]: true }));
    try {
      const { data } = await refreshBrokerConnection(connectionId);
      toast.success("Broker connection refreshed successfully");
      // Open the token view for this connection to show the result
      setShowTokens((prev) => ({ ...prev, [connectionId]: true }));
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to refresh broker connection"));
    } finally {
      setRefreshingToken((prev) => ({ ...prev, [connectionId]: false }));
    }
  };
  
  // Strategies
  const [strategies, setStrategies] = useState([]);
  const [customStrategies, setCustomStrategies] = useState([]);
  const [strategyTemplates, setStrategyTemplates] = useState([]);
  const [catalogStrategies, setCatalogStrategies] = useState([]);
  const [showAddStrategy, setShowAddStrategy] = useState(false);
  const [newStrategy, setNewStrategy] = useState(createEmptyStrategyDraft);
  
  // Risk Settings
  const [riskLoadState, setRiskLoadState] = useState('loading');
  const [riskSaving, setRiskSaving] = useState(false);
  const [riskSettings, setRiskSettings] = useState({
    ...DEFAULT_RISK_SETTINGS
  });
  // Shared with the Dashboard account selector (localStorage key
  // "tp_dashboard_account_id") so Risk Settings always follow the account the
  // user picked on the dashboard. Two-way: changing it here updates that key too.
  const [selectedRiskAccountId, setSelectedRiskAccountId] = useState(
    () => localStorage.getItem("tp_dashboard_account_id") || ""
  );

  const selectedRiskAccountRef = useRef(selectedRiskAccountId);
  selectedRiskAccountRef.current = selectedRiskAccountId;

  // Set the risk-tab account AND mirror it to the shared dashboard selection.
  const handleRiskAccountChange = (accountId) => {
    setSelectedRiskAccountId(accountId);
    if (accountId) {
      localStorage.setItem("tp_dashboard_account_id", accountId);
    }
  };
  
  // Account-Strategy Links
  const [accountStrategies, setAccountStrategies] = useState([]);
  const [showLinkStrategy, setShowLinkStrategy] = useState(false);
  const [newLink, setNewLink] = useState({
    trading_account_id: "",
    strategy_id: "",
    instruments: ["ES"],
    contracts_to_trade: 1,
    is_active: true,
  });

  // User-preferred signal trigger mode
  const [signalTriggerMode, setSignalTriggerMode] = useState(null); // null | 'disabled' | 'bar_close' | 'intrabar'
  const [savingTriggerMode, setSavingTriggerMode] = useState(false);

  const handleSetSignalTriggerMode = async (mode) => {
    setSavingTriggerMode(true);
    try {
      await api.patch('/strategies/auto-trade/signal-trigger-mode', { default_signal_trigger_mode: mode });
      setSignalTriggerMode(mode);
      toast.success(mode ? `Signal trigger mode set to ${mode.replace('_', ' ')}` : 'Signal trigger preference cleared');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save signal trigger mode'));
    } finally {
      setSavingTriggerMode(false);
    }
  };

  // User-level persistent entry settings
  const [persistentEntryDraft, setPersistentEntryDraft] = useState({
    allow_persistent_signal_entry: false,
    persistent_entry_max_age_bars: 6,
    persistent_entry_min_state_bars: 2,
    persistent_entry_cooldown_after_close_bars: 3,
    persistent_entry_max_atr_distance_from_kama: 1.5,
    require_broker_flat: true,
    require_broker_state_confirmed: true,
  });
  const [savingPersistentEntry, setSavingPersistentEntry] = useState(false);

  const handleSavePersistentEntrySettings = async () => {
    setSavingPersistentEntry(true);
    try {
      await api.patch(`/strategies/auto-trade/persistent-entry-settings`, persistentEntryDraft);
      toast.success("Persistent entry settings saved");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save persistent entry settings"));
    } finally {
      setSavingPersistentEntry(false);
    }
  };

  // Per-link persistent entry override toggle state
  const [savingLinkPE, setSavingLinkPE] = useState(null); // link.id while saving

  const handleSetLinkPersistentEntry = async (linkId, value) => {
    // value: true (on) | false (off) | null (inherit from global)
    setSavingLinkPE(linkId);
    try {
      const res = await api.patch(`/strategies/account-strategies/${linkId}/settings`, {
        link_persistent_entry_enabled: value,
      });
      setAccountStrategies((prev) =>
        prev.map((l) =>
          l.id === linkId
            ? { ...l, link_persistent_entry_enabled: res.data?.link_persistent_entry_enabled ?? value }
            : l
        )
      );
      toast.success(
        value === null
          ? "Persistent entry reset to global default"
          : value
          ? "Persistent entry enabled for this link"
          : "Persistent entry disabled for this link"
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update persistent entry override"));
    } finally {
      setSavingLinkPE(null);
    }
  };

  // Auto-trade global state
  const [autoTradeState, setAutoTradeState] = useState(normalizeAutoTradeState());
  const [runningAutomation, setRunningAutomation] = useState(false);
  const [automationRuns, setAutomationRuns] = useState([]);
  const [executionOverview, setExecutionOverview] = useState({
    jobs: {},
    live_trades: {},
    recent_dead_letters: [],
  });
  const [executionJobs, setExecutionJobs] = useState([]);
  const [reconcilingPositions, setReconcilingPositions] = useState(false);
  const [retryingJobId, setRetryingJobId] = useState(null);
  const [tradeAuditFilters, setTradeAuditFilters] = useState(defaultAuditFilters);
  const [tradeAuditRows, setTradeAuditRows] = useState([]);
  const [tradeAuditLoading, setTradeAuditLoading] = useState(false);
  const [tradeAuditError, setTradeAuditError] = useState("");
  const firstAccountIdsByConnection = useMemo(() => {
    const firstIds = new Set();
    const seenConnections = new Set();
    accounts.forEach((account) => {
      if (!seenConnections.has(account.connection_id)) {
        seenConnections.add(account.connection_id);
        firstIds.add(account.id);
      }
    });
    return firstIds;
  }, [accounts]);

  useEffect(() => {
    setUserTimezone(user?.timezone || browserTimezone);
  }, [browserTimezone, user?.timezone]);

  useEffect(() => {
    let active = true;
    getNotificationPreferences()
      .then(({ data }) => {
        if (active) setNotificationPreferences(data);
      })
      .catch((error) => {
        if (active) toast.error(getApiErrorMessage(error, "Failed to load notification preferences"));
      })
      .finally(() => {
        if (active) setLoadingNotificationPreferences(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get("tab");
    if (!requestedTab) {
      setActiveTab("accounts");
      return;
    }

    if (requestedTab === "auto-trade" || requestedTab === "paper-trading" || requestedTab === "live-trading") {
      setActiveTab("automation");
      return;
    }

    if (requestedTab === "appearance") {
      setActiveTab("accounts");
      navigate("/settings?tab=accounts", { replace: true });
      return;
    }

    if (["accounts", "notifications", "strategies", "backtest", "risk", "trade-audit", "automation"].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search, navigate]);

  const updateStrategyEngine = (engine) => {
    setNewStrategy((current) => ({
      ...current,
      strategy_type: engine,
      runtime_params: getDefaultRuntimeParams(engine),
    }));
  };

  const updateStrategyRuntimeParam = (key, value) => {
    setNewStrategy((current) => ({
      ...current,
      runtime_params: {
        ...current.runtime_params,
        [key]: value,
      },
    }));
  };

  const openBlankStrategyModal = () => {
    setNewStrategy(createEmptyStrategyDraft());
    setShowAddStrategy(true);
  };

  const closeStrategyModal = () => {
    setShowAddStrategy(false);
  };

  const handleFlowSelect = (nextKey, href) => {
    setActiveTab(nextKey);
    navigate(href, { replace: true });
  };

  // Dashboard Stats
  const [dashboardStats, setDashboardStats] = useState(null);
  const strategyOptions = useMemo(() => ([
    ...strategies.map((strategy) => ({
      id: strategy.id,
      value: strategy.id,
      label: `${strategy.name} (Visual)`,
      source: "visual",
      language: "visual",
    })),
    ...customStrategies.map((strategy) => ({
      id: strategy.id,
      value: `custom:${strategy.id}`,
      label: `${strategy.name} (${strategy.language === "pinescript" ? "Pine Script" : "Python"})`,
      source: "custom",
      language: strategy.language,
    })),
    ...catalogStrategies.map((strategy) => ({
      id: strategy.id,
      value: `catalog:${strategy.id}`,
      label: `${strategy.name} (Catalog)`,
      source: "catalog",
      language: strategy.language || "python",
    })),
  ]), [strategies, customStrategies, catalogStrategies]);
  const allStrategies = useMemo(
    () => [
      ...strategies.map((strategy) => ({ ...strategy, source: "visual" })),
      ...customStrategies.map((strategy) => ({ ...strategy, source: "custom" })),
    ],
    [strategies, customStrategies]
  );
  const linkInstrumentOptions = useMemo(() => {
    const selectedAccount = accounts.find((account) => account.id === newLink.trading_account_id);
    const brokerSymbols = Array.isArray(selectedAccount?.supported_instruments)
      ? selectedAccount.supported_instruments
      : [];
    const brokerOptions = instrumentOptionsFromSymbols(brokerSymbols);
    return brokerOptions.length ? brokerOptions : getKnownInstrumentCatalog();
  }, [accounts, newLink.trading_account_id]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch broker connections and available brokers
      const [connectionsRes, brokersRes, strategiesRes, customStrategiesRes, templatesRes, catalogRes, accountStrategiesRes, autoTradeRes, automationRunsRes, executionOverviewRes, executionJobsRes] = await Promise.all([
        withFallbackResponse(() => getBrokerConnections(), []),
        withFallbackResponse(() => getBrokers(), []),
        withFallbackResponse(() => getStrategies(), []),
        withFallbackResponse(() => getCustomStrategies(), { strategies: [] }),
        withFallbackResponse(() => getStrategyTemplates(), []),
        withFallbackResponse(() => getStrategyCatalog(), { strategies: [] }),
        withFallbackResponse(() => getAccountStrategies(), []),
        withFallbackResponse(() => getAutoTradeStatus(), { enabled: false, paper_mode: true }),
        withFallbackResponse(() => getAutoTradeRuns({ limit: 8 }), { runs: [] }),
        withFallbackResponse(() => getExecutionOverview(), { jobs: {}, live_trades: {}, recent_dead_letters: [] }),
        withFallbackResponse(() => getExecutionJobs({ limit: 10 }), { jobs: [] }),
      ]);
      
      // Transform broker connections to accounts format for backward compatibility
      const transformedAccounts = [];
      const connections = Array.isArray(connectionsRes.data) ? connectionsRes.data : [];
      
      for (const conn of connections) {
        const tradingAccounts = (conn.trading_accounts || []).filter((acct) => acct.is_active !== false);
        for (const acct of tradingAccounts) {
          transformedAccounts.push({
            id: acct.id,
            connection_id: conn.id,
            name: acct.account_name || acct.name || "Trading Account",
            broker_name: conn.broker_display_name || conn.broker_name || "Unknown Broker",
            username: acct.external_account_id || acct.account_id,
            account_balance: acct.balance || 0,
            daily_pnl: acct.daily_pnl || 0,
            is_active: acct.is_active !== false,
            can_trade: acct.can_trade !== false,
            is_connected: conn.status === "connected",
            topstep_bracket_mode: acct.topstep_bracket_mode || "unknown",
            last_connected: acct.last_synced_at || conn.last_connected_at,
            supported_instruments: conn.supported_instruments || [],
            broker_access_token: conn.broker_access_token,
            broker_token_expires_at: conn.broker_token_expires_at,
            broker_token_refreshed_at: conn.broker_token_refreshed_at,
            adopt_external_trades: Boolean(acct.adopt_external_trades),
            broker_max_loss: acct.broker_max_loss ?? null,
            broker_max_loss_type: acct.broker_max_loss_type || "trailing_drawdown",
          });

        }
      }
      
      setAccounts(transformedAccounts);
      setAvailableBrokers(brokersRes.data || []);
      setStrategies(strategiesRes.data || []);
      setCustomStrategies(customStrategiesRes.data?.strategies || []);
      setStrategyTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      setCatalogStrategies(catalogRes.data?.strategies || []);
      const links = Array.isArray(accountStrategiesRes.data) ? accountStrategiesRes.data : [];
      setAccountStrategies(links);
      const normalizedAutoTrade = normalizeAutoTradeState(autoTradeRes.data);
      setAutoTradeState(normalizedAutoTrade);
      // Sync persistent entry draft and signal trigger mode from loaded settings
      if (autoTradeRes.data) {
        const d = autoTradeRes.data;
        setSignalTriggerMode(d.default_signal_trigger_mode ?? null);
        setPersistentEntryDraft({
          allow_persistent_signal_entry: d.allow_persistent_signal_entry ?? false,
          persistent_entry_max_age_bars: d.persistent_entry_max_age_bars ?? 6,
          persistent_entry_min_state_bars: d.persistent_entry_min_state_bars ?? 2,
          persistent_entry_cooldown_after_close_bars: d.persistent_entry_cooldown_after_close_bars ?? 3,
          persistent_entry_max_atr_distance_from_kama: d.persistent_entry_max_atr_distance_from_kama ?? 1.5,
          require_broker_flat: d.require_broker_flat ?? true,
          require_broker_state_confirmed: d.require_broker_state_confirmed ?? true,
        });
      }
      setAutomationRuns(automationRunsRes.data?.runs || []);
      setExecutionOverview(executionOverviewRes.data || { jobs: {}, live_trades: {}, recent_dead_letters: [] });
      setExecutionJobs(executionJobsRes.data?.jobs || []);
      setDashboardStats({
        active_accounts: transformedAccounts.filter((account) => account.is_active && account.is_connected).length,
        total_accounts: transformedAccounts.length,
        active_strategies: links.filter((link) => link.is_active).length,
        total_strategies: (strategiesRes.data || []).length + (customStrategiesRes.data?.strategies || []).length,
        total_balance: transformedAccounts.reduce((sum, account) => sum + Number(account.account_balance || 0), 0),
        total_daily_pnl: transformedAccounts.reduce((sum, account) => sum + Number(account.daily_pnl || 0), 0),
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!accounts.length) {
      setSelectedRiskAccountId("");
      setRiskSettings({ ...DEFAULT_RISK_SETTINGS });
      return;
    }

    const selectedStillExists = accounts.some((account) => account.id === selectedRiskAccountId);
    if (!selectedRiskAccountId || !selectedStillExists) {
      // Prefer the account chosen on the dashboard (shared key); fall back to
      // the first account only when that is missing/invalid. Persist the choice
      // so the dashboard and risk tab stay in sync.
      const shared = localStorage.getItem("tp_dashboard_account_id");
      const sharedValid = shared && accounts.some((account) => account.id === shared);
      handleRiskAccountChange(sharedValid ? shared : accounts[0].id);
    }
  }, [accounts, selectedRiskAccountId]);

  useEffect(() => {
    let active = true;
    const loadRiskSettings = async () => {
      setRiskLoadState('loading');
      const selectedAccount = accounts.find((account) => account.id === selectedRiskAccountId);
      if (!selectedAccount?.connection_id) {
        setRiskSettings({ ...DEFAULT_RISK_SETTINGS });
        return;
      }

      try {
        const response = await api.get(
          `/broker-connections/${selectedAccount.connection_id}/accounts/${selectedAccount.id}/risk-settings`
        );
        if (active) {
          setRiskSettings(mapApiRiskToUi(response.data));
          setRiskLoadState(response.data.configured === false || !response.data.id ? 'unsaved' : 'saved');
        }
      } catch (error) {
        console.error("Error loading risk settings:", error);
        if (active) setRiskLoadState('error');
      }
    };

    if (selectedRiskAccountId) {
      loadRiskSettings();
    }
    return () => { active = false; };
  }, [accounts, selectedRiskAccountId]);

  // Account handlers - Connect to Broker
  const handleAddAccount = async () => {
    if (!newAccount.broker_type || !newAccount.username || !newAccount.api_key) {
      toast.error("Please fill in all fields");
      return;
    }
    
    try {
      // Use the new broker-connections/connect API
      const selectedBroker = availableBrokers.find(b => b.id === parseInt(newAccount.broker_type) || b.name === newAccount.broker_type);
      const brokerName = selectedBroker?.name || 'topstepx';
      
      const res = await connectBroker({
        broker_name: brokerName,
        credentials: {
          username: newAccount.username,
          api_key: newAccount.api_key
        }
      });
      
      // Show success with account count
      const accountCount = res.data.trading_accounts?.length || 0;
      toast.success(`Connected to ${res.data.broker_display_name}! Found ${accountCount} trading account(s).`);
      
      setNewAccount({ name: "", broker_type: "topstepx", username: "", api_key: "" });
      setShowAddAccount(false);
      
      // Refresh data to show new accounts
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to connect to broker"));
    }
  };

  const handleSaveUserPreferences = async () => {
    setSavingUserPreferences(true);
    try {
      const response = await authUpdateMe({
        timezone: userTimezone,
        preferences: {
          discord_webhook_url: discordWebhookUrl,
        }
      });
      setUser(response.data);
      toast.success("Preferences updated");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save preferences"));
    } finally {
      setSavingUserPreferences(false);
    }
  };

  const handleSaveNotificationPreferences = async () => {
    setSavingNotificationPreferences(true);
    try {
      const { data } = await updateNotificationPreferences({
        email_enabled: notificationPreferences.email_enabled,
        telegram_enabled: notificationPreferences.telegram_enabled,
        telegram_chat_id: notificationPreferences.telegram_chat_id,
      });
      setNotificationPreferences(data);
      setUser((currentUser) => currentUser ? {
        ...currentUser,
        preferences: {
          ...(currentUser.preferences || {}),
          alert_email_enabled: data.email_enabled,
          alert_telegram_enabled: data.telegram_enabled,
          telegram_chat_id: data.telegram_chat_id,
        },
      } : currentUser);
      toast.success("Notification preferences updated");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save notification preferences"));
    } finally {
      setSavingNotificationPreferences(false);
    }
  };

  const saveNotificationPreferencesForTest = async () => {
    const { data } = await updateNotificationPreferences({
      email_enabled: notificationPreferences.email_enabled,
      telegram_enabled: notificationPreferences.telegram_enabled,
      telegram_chat_id: notificationPreferences.telegram_chat_id,
    });
    setNotificationPreferences(data);
    return data;
  };

  const handleTestNotificationChannel = async (channel) => {
    setTestingNotificationChannel(channel);
    try {
      await saveNotificationPreferencesForTest();
      if (channel === "telegram") {
        await sendTestTelegramNotification();
        const { data } = await getNotificationPreferences();
        setNotificationPreferences(data);
        toast.success("Test Telegram alert sent");
      } else {
        await sendTestEmailNotification();
        toast.success("Test email alert sent");
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, `Failed to send test ${channel} alert`));
    } finally {
      setTestingNotificationChannel("");
    }
  };

  const refreshExecutionState = useCallback(async () => {
    try {
      const [overviewRes, jobsRes, runsRes, autoTradeRes] = await Promise.all([
        getExecutionOverview(),
        getExecutionJobs({ limit: 10 }),
        getAutoTradeRuns({ limit: 8 }),
        getAutoTradeStatus(),
      ]);
      setExecutionOverview(overviewRes.data || { jobs: {}, live_trades: {}, recent_dead_letters: [] });
      setExecutionJobs(jobsRes.data?.jobs || []);
      setAutomationRuns(runsRes.data?.runs || []);
      setAutoTradeState(normalizeAutoTradeState(autoTradeRes.data));
      if (autoTradeRes.data) {
        const d = autoTradeRes.data;
        if (d.default_signal_trigger_mode !== undefined) setSignalTriggerMode(d.default_signal_trigger_mode ?? null);
        setPersistentEntryDraft((prev) => ({
          ...prev,
          allow_persistent_signal_entry: d.allow_persistent_signal_entry ?? prev.allow_persistent_signal_entry,
          persistent_entry_max_age_bars: d.persistent_entry_max_age_bars ?? prev.persistent_entry_max_age_bars,
          persistent_entry_min_state_bars: d.persistent_entry_min_state_bars ?? prev.persistent_entry_min_state_bars,
          persistent_entry_cooldown_after_close_bars: d.persistent_entry_cooldown_after_close_bars ?? prev.persistent_entry_cooldown_after_close_bars,
          persistent_entry_max_atr_distance_from_kama: d.persistent_entry_max_atr_distance_from_kama ?? prev.persistent_entry_max_atr_distance_from_kama,
          require_broker_flat: d.require_broker_flat ?? prev.require_broker_flat,
          require_broker_state_confirmed: d.require_broker_state_confirmed ?? prev.require_broker_state_confirmed,
        }));
      }
    } catch (error) {
      console.error("Error refreshing execution state:", error);
    }
  }, []);

  const loadTradeAudit = useCallback(async (overrides = {}) => {
    const filters = { ...tradeAuditFilters, ...overrides };
    setTradeAuditLoading(true);
    setTradeAuditError("");
    try {
      const params = {
        limit: 80,
        ...(filters.q?.trim() ? { q: filters.q.trim() } : {}),
        ...(filters.trading_account_id && filters.trading_account_id !== "all"
          ? { trading_account_id: filters.trading_account_id }
          : {}),
        ...(filters.start ? { start: filters.start } : {}),
        ...(filters.end ? { end: filters.end } : {}),
      };
      const { data } = await api.get("/trades/audit-search", { params });
      setTradeAuditRows(Array.isArray(data?.trades) ? data.trades : []);
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to load trade audit");
      setTradeAuditError(message);
      toast.error(message);
    } finally {
      setTradeAuditLoading(false);
    }
  }, [tradeAuditFilters]);

  useEffect(() => {
    if (activeTab === "trade-audit") {
      loadTradeAudit();
    }
  }, [activeTab, loadTradeAudit]);

  const handleDeleteAccount = async (accountId, connectionId) => {
    if (!window.confirm("Disconnect this broker connection and remove all linked accounts from TradoPro?")) return;
    
    try {
      // Delete the broker connection
      await deleteBrokerConnection(connectionId);
      toast.success("Broker disconnected");
      fetchData();
    } catch (error) {
      toast.error("Failed to disconnect broker");
    }
  };

  const handleConnectAccount = async (connectionId) => {
    setConnectingAccount(connectionId);
    try {
      const res = await refreshBrokerConnection(connectionId);
      toast.success(`Refreshed! ${res.data.accounts_synced} account(s) synced.`);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Refresh failed"));
    } finally {
      setConnectingAccount(null);
    }
  };

  const handleUpdateAccountSettings = async (connectionId, accountId, settings) => {
    try {
      await api.put(`/broker-connections/${connectionId}/accounts/${accountId}`, settings);
      toast.success("Account settings updated");
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update account settings"));
    }
  };

  // Strategy handlers
  const handleAddStrategy = async () => {
    if (!newStrategy.name) {
      toast.error("Please enter a strategy name");
      return;
    }
    
    try {
      if (newStrategy.mode === "custom") {
        await api.post(`/strategies/custom`, {
          name: newStrategy.name,
          description: newStrategy.description,
          language: newStrategy.language,
          code: newStrategy.code,
          runtime: buildRuntimeDefinition(
            newStrategy.strategy_type,
            newStrategy.runtime_params,
            newStrategy.language,
            "custom"
          ),
        });
      } else {
        const payload = {
          name: newStrategy.name,
          description: newStrategy.description,
          indicators: normalizeIndicatorsForApi(newStrategy.indicators),
          conditions: {
            strategy_type: newStrategy.strategy_type,
            entry: newStrategy.entry_conditions || {},
            exit: newStrategy.exit_conditions || {},
          },
          runtime: buildRuntimeDefinition(
            newStrategy.strategy_type,
            newStrategy.runtime_params,
            "visual",
            "visual"
          ),
        };
        await api.post(`/strategies/`, payload);
      }
      setNewStrategy(createEmptyStrategyDraft());
      setShowAddStrategy(false);
      fetchData();
      toast.success("Strategy created");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to create strategy"));
    }
  };

  const handleDeleteStrategy = async (strategyId) => {
    if (!window.confirm("Are you sure you want to delete this strategy?")) return;
    
    try {
      if (strategyId.startsWith("custom:")) {
        const id = strategyId.replace("custom:", "");
        await api.delete(`/strategies/custom/${id}`);
      } else {
        await api.delete(`/strategies/${strategyId}`);
      }
      fetchData();
      toast.success("Strategy deleted");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete strategy"));
    }
  };

  const handleUseTemplate = (template) => {
    const engine = inferStrategyEngine(template.name, template.conditions?.strategy_type);
    setNewStrategy({
      mode: "visual",
      name: template.name,
      description: template.description,
      language: "pinescript",
      code: "",
      strategy_type: engine,
      runtime_params: getDefaultRuntimeParams(engine),
      indicators: normalizeIndicatorsForApi(template.indicators || []),
      entry_conditions: template.conditions?.entry || {},
      exit_conditions: template.conditions?.exit || {}
    });
    setShowAddStrategy(true);
  };

  const handleCloneCatalogStrategy = async (catalogId) => {
    try {
      await api.post(`/strategies/catalog/${catalogId}/clone`);
      toast.success("Catalog strategy added to your custom scripts");
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to add catalog strategy"));
    }
  };

  // Risk settings handler
  const handleSaveRiskSettings = async () => {
    if (riskSaving || riskLoadState === 'loading' || riskLoadState === 'error') return;
    const selectedAccount = accounts.find((account) => account.id === selectedRiskAccountId);
    if (!selectedAccount?.connection_id) {
      toast.error("Select a trading account first");
      return;
    }

    setRiskSaving(true);
    try {
      await api.put(
        `/broker-connections/${selectedAccount.connection_id}/accounts/${selectedAccount.id}/risk-settings`,
        mapUiRiskToApi(riskSettings)
      );
      toast.success(`Risk settings saved for ${selectedAccount.name}`);
      if (selectedRiskAccountRef.current === selectedAccount.id) setRiskLoadState('saved');
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save risk settings"));
    } finally {
      setRiskSaving(false);
    }
  };

  // Account-Strategy Link handlers
  const handleLinkStrategy = async () => {
    if (!newLink.trading_account_id || !newLink.strategy_id) {
      toast.error("Please select an account and strategy");
      return;
    }
    const selectedAccount = accounts.find((account) => account.id === newLink.trading_account_id);
    if (selectedAccount?.can_trade === false) {
      toast.error("This broker account is ineligible for trading right now");
      return;
    }
    const familyConflict = newLink.instruments.reduce(
      (conflict, symbol) => conflict || findInstrumentFamilyConflict(newLink.instruments.filter((entry) => entry !== symbol), symbol),
      null,
    );
    if (familyConflict) {
      toast.error(
        `Choose either ${familyConflict.existingSymbol} or ${familyConflict.nextSymbol} for this setup. Mini and micro contracts from the same market cannot be linked together.`,
      );
      return;
    }

    try {
      const response = await api.post(`/strategies/account-strategies`, newLink);
      setNewLink({ trading_account_id: "", strategy_id: "", instruments: ["ES"], contracts_to_trade: 1, is_active: true });
      setShowLinkStrategy(false);
      fetchData();
      toast.success(
        response.data?.count > 1
          ? `Strategy linked to ${response.data.count} contracts`
          : "Strategy linked to contract",
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to link strategy"));
    }
  };

  const toggleLinkInstrument = (nextSymbol) => {
    setNewLink((current) => {
      if (current.instruments.includes(nextSymbol)) {
        return {
          ...current,
          instruments: current.instruments.filter((symbol) => symbol !== nextSymbol),
        };
      }

      const conflict = findInstrumentFamilyConflict(current.instruments, nextSymbol);
      if (!conflict) {
        return {
          ...current,
          instruments: [...current.instruments, nextSymbol],
        };
      }

      toast.warning(
        `Choose either ${conflict.existingSymbol} or ${conflict.nextSymbol}. Replacing ${conflict.existingSymbol} with ${conflict.nextSymbol} for this setup.`,
      );
      return {
        ...current,
        instruments: current.instruments
          .filter((symbol) => symbol !== conflict.existingSymbol)
          .concat(conflict.nextSymbol),
      };
    });
  };

  const handleUnlinkStrategy = async (linkId) => {
    try {
      await api.delete(`/strategies/account-strategies/${linkId}`);
      fetchData();
      toast.success("Strategy unlinked");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to unlink strategy"));
    }
  };

  const handleToggleLink = async (linkId) => {
    try {
      const res = await api.patch(`/strategies/account-strategies/${linkId}/toggle`);
      fetchData();
      toast.success(res.data.is_active ? "Automation enabled" : "Automation paused");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to toggle automation"));
    }
  };

  const handleToggleAutoTrade = async () => {
    try {
      const res = await api.post(`/strategies/auto-trade/toggle`, { enabled: !autoTradeState.enabled });
      setAutoTradeState(prev => ({ ...prev, ...normalizeAutoTradeState(res.data) }));
      refreshExecutionState();
      toast.success(res.data.enabled ? "Auto-trading enabled" : "Auto-trading paused");
    } catch (error) {
      toast.error("Failed to toggle auto-trading");
    }
  };

  const handleTogglePaperMode = async () => {
    try {
      const res = await api.post(`/strategies/auto-trade/paper-mode`, { paper_mode: !autoTradeState.paper_mode });
      setAutoTradeState(prev => ({ ...prev, ...normalizeAutoTradeState(res.data) }));
      refreshExecutionState();
      toast.success(res.data.paper_mode ? "Paper mode on" : "Live mode on");
    } catch (error) {
      toast.error("Failed to toggle mode");
    }
  };

  const handleToggleAutomationAccount = async (accountId, enabled) => {
    const selectedAccount = accounts.find((account) => account.id === accountId);
    if (enabled && selectedAccount?.can_trade === false) {
      toast.error("This broker account is ineligible for auto-trading right now");
      return;
    }
    const nextAccountIds = enabled
      ? Array.from(new Set([...(autoTradeState.enabled_account_ids || []), accountId]))
      : (autoTradeState.enabled_account_ids || []).filter((id) => id !== accountId);

    try {
      const res = await api.put(`/strategies/auto-trade/accounts`, { account_ids: nextAccountIds });
      setAutoTradeState((prev) => ({ ...prev, ...normalizeAutoTradeState(res.data) }));
      toast.success(
        enabled ? "Account added to auto-trade scope" : "Account removed from auto-trade scope"
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update auto-trade account scope"));
    }
  };

  const handleToggleAdoptExternalTrades = async (accountId, enabled) => {
    try {
      await patchAdoptExternalTrades(accountId, enabled);
      setAccounts((prev) =>
        prev.map((a) => a.id === accountId ? { ...a, adopt_external_trades: enabled } : a)
      );
      toast.success(`TradoPro Takes Control ${enabled ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update position adoption"));
    }
  };

  const handleRunAutomation = async () => {
    setRunningAutomation(true);
    try {
      const res = await api.post(`/strategies/auto-trade/run`, {});
      const links = res.data?.links || [];
      const results = links.flatMap((link) => link.results || []);
      const paperExecuted = results.filter((row) => row.status === "executed" && row.mode === "paper").length;
      const liveQueued = results.filter((row) => row.status === "queued" && row.mode === "live").length;
      const liveFailed = results.filter((row) => row.status === "failed" && row.mode === "live").length;
      const signalOnly = results.filter((row) => row.status === "signal_only").length;
      setAutoTradeState((prev) => ({
        ...prev,
        last_scan_at: res.data?.last_scan_at || prev.last_scan_at,
        last_execution_at: res.data?.last_execution_at || prev.last_execution_at,
      }));
      toast.success(
        liveQueued > 0
          ? `Automation run completed: ${liveQueued} live order(s) queued to broker`
          : paperExecuted > 0
            ? `Automation run completed: ${paperExecuted} paper trade(s) opened`
            : signalOnly > 0
              ? `Automation scan completed: ${signalOnly} signal(s) found, but no broker order was sent`
              : liveFailed > 0
                ? `Automation run completed: ${liveFailed} live order(s) failed to queue`
                : "Automation scan completed"
      );
      refreshExecutionState();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to run automation"));
    } finally {
      setRunningAutomation(false);
    }
  };

  const handleReconcilePositions = async () => {
    setReconcilingPositions(true);
    try {
      const res = await api.post(`/positions/reconcile`, {});
      const totalAffected = (res.data?.accounts || []).reduce(
        (sum, account) => sum + Number(account.created || 0) + Number(account.updated || 0) + Number(account.closed || 0),
        0
      );
      await refreshExecutionState();
      toast.success(
        totalAffected > 0
          ? `Reconciliation completed: ${totalAffected} position updates`
          : "Reconciliation completed with no ledger changes"
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to reconcile positions"));
    } finally {
      setReconcilingPositions(false);
    }
  };

  const handleRetryExecutionJob = async (jobId) => {
    setRetryingJobId(jobId);
    try {
      await api.post(`/execution/jobs/${jobId}/retry`);
      await refreshExecutionState();
      toast.success("Execution job re-queued");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to retry execution job"));
    } finally {
      setRetryingJobId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--app-shell-gradient)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <PlatformShell
      title="Settings"
      titleIcon={SettingsIcon}
      backTo="/dashboard"
      currentKey={activeTab === "backtest" ? "backtest" : "settings"}
    >
      {/* Stats Bar */}
      {dashboardStats && (
        <div className="border-b border-zinc-800 bg-zinc-900/30 px-4 py-3 sm:px-6">
          <div className="grid grid-cols-2 gap-3 text-xs sm:flex sm:gap-6 sm:text-sm">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-blue-400" />
              <span className="text-zinc-400">Accounts:</span>
              <span className="font-mono">{dashboardStats.active_accounts}/{dashboardStats.total_accounts}</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span className="text-zinc-400">Strategies:</span>
              <span className="font-mono">{dashboardStats.active_strategies}/{dashboardStats.total_strategies}</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              <span className="text-zinc-400">Total Balance:</span>
              <span className="font-mono">${dashboardStats.total_balance?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-400" />
              <span className="text-zinc-400">Today's P&L:</span>
              <span className={`font-mono ${dashboardStats.total_daily_pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {dashboardStats.total_daily_pnl >= 0 ? '+' : ''}${dashboardStats.total_daily_pnl?.toFixed(2) || '0.00'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4 sm:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <SettingsFlowNav activeKey={activeTab} onSelect={handleFlowSelect} />

          <TabsContent value="notifications" className="space-y-4">
            <div>
              <h2 className="text-xl font-bold">Notifications</h2>
              <p className="mt-1 text-sm text-zinc-400">Choose email, Telegram, both, or neither.</p>
            </div>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Bell className="h-4 w-4 text-blue-400" /> Alert Channels
                </CardTitle>
                <CardDescription>These preferences belong to your user account.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {loadingNotificationPreferences ? (
                  <div className="flex min-h-28 items-center justify-center text-zinc-400">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading preferences
                  </div>
                ) : (
                  <>
                    <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-4 text-sm text-zinc-300">
                      <p className="font-medium text-zinc-100">Alerts you will receive</p>
                      <p className="mt-2 text-xs leading-5 text-zinc-400">
                        Futures market summaries at 8:30 AM, 9:30 AM, 10:00 AM, 12:00 PM, and 3:30 PM Eastern;
                        BUY/SELL signals from your selected strategy; and confirmed entries from Auto-Trade or
                        TradoPro Takes Control; and managed position closures with exit reasons. No other alerts are sent.
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-4 rounded-md border border-zinc-800 bg-zinc-950/60 p-4">
                      <div className="flex min-w-0 items-start gap-3">
                        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" />
                        <div>
                          <Label htmlFor="alert-email-enabled" className="text-sm font-medium">Email alerts</Label>
                          <p className="mt-1 text-xs text-zinc-400">Send alerts to {user?.email || "your account email"}.</p>
                        </div>
                      </div>
                      <Switch
                        id="alert-email-enabled"
                        checked={notificationPreferences.email_enabled}
                        onCheckedChange={(checked) => setNotificationPreferences((current) => ({ ...current, email_enabled: checked }))}
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        onClick={() => handleTestNotificationChannel("email")}
                        disabled={!notificationPreferences.email_enabled || Boolean(testingNotificationChannel)}
                      >
                        {testingNotificationChannel === "email" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Send Test Email
                      </Button>
                    </div>

                    <div className="space-y-4 rounded-md border border-zinc-800 bg-zinc-950/60 p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 items-start gap-3">
                          <Send className="mt-0.5 h-5 w-5 shrink-0 text-sky-400" />
                          <div>
                            <Label htmlFor="alert-telegram-enabled" className="text-sm font-medium">Telegram alerts</Label>
                            <p className="mt-1 text-xs text-zinc-400">
                              {notificationPreferences.telegram_bot_configured
                                ? "Send alerts through the TradoPro Telegram bot."
                                : "Telegram bot delivery is not configured on this server."}
                            </p>
                          </div>
                        </div>
                        <Switch
                          id="alert-telegram-enabled"
                          checked={notificationPreferences.telegram_enabled}
                          disabled={!notificationPreferences.telegram_bot_configured}
                          onCheckedChange={(checked) => setNotificationPreferences((current) => ({ ...current, telegram_enabled: checked }))}
                        />
                      </div>
                      <div className="max-w-sm space-y-2">
                        <Label htmlFor="telegram-chat-id">Telegram chat ID</Label>
                        <div className="relative">
                          <Input
                            id="telegram-chat-id"
                            type={showTelegramChatId ? "text" : "password"}
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder="Example: 123456789"
                            value={notificationPreferences.telegram_chat_id}
                            disabled={!notificationPreferences.telegram_enabled}
                            onChange={(event) => setNotificationPreferences((current) => ({
                              ...current,
                              telegram_chat_id: event.target.value,
                            }))}
                            className="bg-zinc-800 border-zinc-700 pr-10"
                          />
                          <button
                            type="button"
                            aria-label={showTelegramChatId ? "Hide Telegram chat ID" : "Show Telegram chat ID"}
                            disabled={!notificationPreferences.telegram_enabled}
                            onClick={() => setShowTelegramChatId((visible) => !visible)}
                            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-zinc-400 hover:text-zinc-200 disabled:opacity-40"
                          >
                            {showTelegramChatId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {notificationPreferences.telegram_verified_at && (
                          <p className="text-xs text-emerald-400">Telegram verified</p>
                        )}
                      </div>
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          onClick={() => handleTestNotificationChannel("telegram")}
                          disabled={
                            !notificationPreferences.telegram_enabled
                            || !notificationPreferences.telegram_chat_id
                            || Boolean(testingNotificationChannel)
                          }
                        >
                          {testingNotificationChannel === "telegram" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Send Test Telegram Alert
                        </Button>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button
                        onClick={handleSaveNotificationPreferences}
                        disabled={savingNotificationPreferences || Boolean(testingNotificationChannel)}
                      >
                        {savingNotificationPreferences ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                        Save Notification Preferences
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trading Accounts Tab */}
          <TabsContent value="accounts" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Trading Accounts</h2>
              <Button onClick={() => setShowAddAccount(true)} data-testid="add-account-btn">
                <PlugZap className="w-4 h-4 mr-2" />
                Connect Broker
              </Button>
            </div>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base">User Preferences</CardTitle>
                  <CardDescription>
                    All displayed timestamps in TradoPro use this timezone where the UI supports user preferences.
                  </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-[minmax(0,320px)_1fr] md:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="app-timezone">App Timezone</Label>
                    <Select value={userTimezone} onValueChange={setUserTimezone}>
                      <SelectTrigger id="app-timezone" className="bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Choose timezone..." />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {timezoneOptions.map((timezoneOption) => (
                          <SelectItem key={timezoneOption} value={timezoneOption}>
                            {timezoneOption}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-md border border-zinc-800 bg-zinc-950/70 p-3 text-sm text-zinc-400">
                    Browser timezone detected: <span className="font-medium text-zinc-200">{browserTimezone}</span>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-[minmax(0,320px)_1fr] md:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="discord-webhook">Discord Webhook URL</Label>
                    <Input
                      id="discord-webhook"
                      placeholder="https://discord.com/api/webhooks/..."
                      value={discordWebhookUrl}
                      onChange={(e) => setDiscordWebhookUrl(e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="rounded-md border border-zinc-800 bg-zinc-950/70 p-3 text-xs text-zinc-400 italic">
                    Paste your Discord channel webhook URL here to receive real-time trade alerts.
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveUserPreferences} disabled={savingUserPreferences}>
                    {savingUserPreferences ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Add Account Dialog */}
            <Dialog open={showAddAccount} onOpenChange={setShowAddAccount}>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle>Connect to Broker</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Select Broker</Label>
                    <Select
                      value={newAccount.broker_type}
                      onValueChange={(value) => setNewAccount({ ...newAccount, broker_type: value })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="broker-select">
                        <SelectValue placeholder="Select broker" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {availableBrokers.map((broker) => (
                          <SelectItem key={broker.id} value={broker.name}>
                            <div className="flex items-center gap-2">
                              <span>{broker.display_name}</span>
                              {broker.trading_hours && (
                                <Badge variant="outline" className="text-[9px]">
                                  {broker.trading_hours.start}-{broker.trading_hours.end}
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {availableBrokers.find(b => b.name === newAccount.broker_type)?.description && (
                      <p className="text-xs text-zinc-400">
                        {availableBrokers.find(b => b.name === newAccount.broker_type)?.description}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      placeholder="Your broker username"
                      value={newAccount.username}
                      onChange={(e) => setNewAccount({ ...newAccount, username: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="account-username-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      placeholder="Your API Key"
                      value={newAccount.api_key}
                      onChange={(e) => setNewAccount({ ...newAccount, api_key: e.target.value })}
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="account-apikey-input"
                    />
                    <p className="text-xs text-zinc-400">Get your API key from your broker's dashboard</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddAccount(false)}>Cancel</Button>
                  <Button onClick={handleAddAccount} data-testid="connect-broker-btn">
                    <PlugZap className="w-4 h-4 mr-2" />
                    Connect
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Accounts List */}
            <div className="grid gap-4">
              {accounts.length === 0 ? (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Key className="w-12 h-12 text-zinc-400 mb-4" />
                    <p className="text-zinc-400 mb-4">No broker connections configured</p>
                    <Button onClick={() => setShowAddAccount(true)}>
                      <PlugZap className="w-4 h-4 mr-2" />
                      Connect Your First Broker
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                accounts.map((account) => {
                  const showsConnectionActions = firstAccountIdsByConnection.has(account.id);
                  return (
                  <Card key={account.id} className="bg-zinc-900 border-zinc-800">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${account.is_connected ? 'bg-green-500' : 'bg-zinc-500'}`} />
                          <CardTitle className="text-lg">{account.name}</CardTitle>
                          <Badge variant="outline">{account.broker_name}</Badge>
                          <Badge variant={account.is_active ? "default" : "secondary"}>
                            {account.is_active ? "Active" : "Inactive"}
                          </Badge>
                          {account.can_trade === false && (
                            <Badge variant="destructive">Ineligible</Badge>
                          )}
                        </div>
                        {showsConnectionActions ? (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConnectAccount(account.connection_id)}
                              disabled={connectingAccount === account.connection_id}
                              data-testid={`refresh-account-${account.id}`}
                            >
                              {connectingAccount === account.connection_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                  Refresh Accounts
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRefreshBroker(account.connection_id)}
                              disabled={refreshingToken[account.connection_id]}
                              data-testid={`refresh-token-${account.id}`}
                            >
                              {refreshingToken[account.connection_id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <KeyRound className="w-4 h-4 mr-1" />
                                  Refresh Token (All Accounts)
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteAccount(account.id, account.connection_id)}
                              data-testid={`delete-account-${account.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Disconnect Broker
                            </Button>
                          </div>
                        ) : (
                          <div className="text-xs text-zinc-400">
                            Uses shared broker connection actions above
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {showsConnectionActions && (
                        <div className="mb-4 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs text-zinc-400">
                          Connection-level actions apply to all trading accounts returned by this broker login.
                        </div>
                      )}
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-zinc-400">Account ID</span>
                          <p className="font-mono">{account.username}</p>
                        </div>
                        <div>
                          <span className="text-zinc-400">Balance</span>
                          <p className="font-mono text-green-400">
                            ${account.account_balance?.toFixed(2) || '--'}
                          </p>
                        </div>
                        <div>
                          <span className="text-zinc-400">Daily P&L</span>
                          <p className={`font-mono ${(account.daily_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {(account.daily_pnl || 0) >= 0 ? '+' : ''}${account.daily_pnl?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <div>
                          <span className="text-zinc-400">Last Synced</span>
                          <p className="font-mono text-xs">
                            {account.last_connected ? formatDateTimeDisplay(account.last_connected, appTimeZone) : 'Never'}
                          </p>
                        </div>
                      </div>

                      {/* Portfolio Management — broker-authorized max loss */}
                      <div className="mt-4 p-3 bg-zinc-950/50 rounded-lg border border-zinc-800">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-zinc-300">Portfolio Management</span>
                        </div>
                        <p className="text-xs text-zinc-500 mb-3">
                          Broker-authorized maximum loss for this account (e.g. Topstep trailing drawdown).
                          Used to size contracts and risk parameters. Informational — it does not place orders.
                        </p>
                        <div className="grid grid-cols-2 gap-3 items-end">
                          <div>
                            <label className="text-xs text-zinc-400">Max loss authorized ($)</label>
                            <Input
                              type="number"
                              min="0"
                              step="50"
                              defaultValue={account.broker_max_loss ?? ""}
                              placeholder="e.g. 2000"
                              data-testid={`broker-max-loss-${account.id}`}
                              onBlur={(e) => {
                                const raw = e.target.value.trim();
                                const val = raw === "" ? -1 : Number(raw);
                                if (Number.isNaN(val)) return;
                                if (val === (account.broker_max_loss ?? -1)) return;
                                handleUpdateAccountSettings(account.connection_id, account.id, { broker_max_loss: val });
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-zinc-400">Type</label>
                            <Select
                              value={account.broker_max_loss_type || "trailing_drawdown"}
                              onValueChange={(val) => handleUpdateAccountSettings(account.connection_id, account.id, { broker_max_loss_type: val })}
                            >
                              <SelectTrigger data-testid={`broker-max-loss-type-${account.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="trailing_drawdown">Trailing drawdown</SelectItem>
                                <SelectItem value="static_total">Static total</SelectItem>
                                <SelectItem value="daily">Daily limit</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Mirroring / Copy-trading Section */}
                      <div className="mt-4 p-3 bg-zinc-950/50 rounded-lg border border-zinc-800">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-medium text-zinc-300">Copy-Trading (Mirroring)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label htmlFor={`master-toggle-${account.id}`} className="text-xs text-zinc-400">Set as Master</Label>
                            <Switch
                              id={`master-toggle-${account.id}`}
                              checked={account.is_master}
                              disabled={account.can_trade === false}
                              onCheckedChange={(checked) => handleUpdateAccountSettings(account.connection_id, account.id, { is_master: checked })}
                            />
                          </div>
                        </div>

                        {!account.is_master && (
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-zinc-400 whitespace-nowrap">Follow Master:</span>
                            <Select
                              value={account.master_account_id || "none"}
                              disabled={account.can_trade === false}
                              onValueChange={(val) => handleUpdateAccountSettings(account.connection_id, account.id, { master_account_id: val === "none" ? null : val })}
                            >
                              <SelectTrigger className="h-8 bg-zinc-800 border-zinc-700 text-xs">
                                <SelectValue placeholder="Select master account..." />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-zinc-800">
                                <SelectItem value="none">None (Individual)</SelectItem>
                                {accounts.filter(a => a.is_master && a.id !== account.id).map(master => (
                                  <SelectItem key={master.id} value={master.id}>{master.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {account.is_master && (
                          <div className="text-xs text-purple-400/80 italic">
                            This is a Master account. Trades placed here will be duplicated to all followers.
                          </div>
                        )}
                      </div>
                      {/* Token Info Section - always visible */}
                      {account.broker_access_token && (
                        <div className="mt-4 p-3 bg-zinc-800 rounded-lg border border-zinc-700">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                              <KeyRound className="w-4 h-4" />
                              Token Details
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowTokens((prev) => ({ ...prev, [account.connection_id]: !prev[account.connection_id] }))}
                            >
                              {showTokens[account.connection_id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                          </div>
                          <div className="space-y-2 text-sm">
                            {showTokens[account.connection_id] && (
                              <div>
                                <span className="text-zinc-400">Access Token</span>
                                <p className="font-mono text-xs break-all text-zinc-300">
                                  {account.broker_access_token.slice(0, 20)}...{account.broker_access_token.slice(-10)}
                                </p>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <span className="text-zinc-400">Expires At</span>
                                <p className="font-mono text-xs">
                                  {account.broker_token_expires_at
                                    ? formatDateTimeDisplay(account.broker_token_expires_at, appTimeZone)
                                    : "Unknown"}
                                </p>
                              </div>
                              <div>
                                <span className="text-zinc-400">Last Refreshed</span>
                                <p className="font-mono text-xs">
                                  {account.broker_token_refreshed_at
                                    ? formatDateTimeDisplay(account.broker_token_refreshed_at, appTimeZone)
                                    : "Never"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )})
              )}
            </div>
          </TabsContent>

          {/* Strategies Tab */}
          <TabsContent value="strategies" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Trading Strategies</h2>
                <p className="mt-1 text-sm text-zinc-400">
                  Create or import a strategy here, then go to <span className="text-white">Automation</span> to attach it to an account.
                </p>
              </div>
              <Button onClick={openBlankStrategyModal} data-testid="add-strategy-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Strategy
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-zinc-400">Step 1</div>
                  <div className="mt-2 font-semibold text-white">Choose a starting point</div>
                  <p className="mt-1 text-sm text-zinc-400">
                    Use a template, clone a catalog strategy, or create one from scratch.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-zinc-400">Step 2</div>
                  <div className="mt-2 font-semibold text-white">Save it to your library</div>
                  <p className="mt-1 text-sm text-zinc-400">
                    Pick visual builder, Pine Script, or Python, then save the strategy here.
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-zinc-400">Step 3</div>
                  <div className="mt-2 font-semibold text-white">Link it in Automation</div>
                  <p className="mt-1 text-sm text-zinc-400">
                    Go to Automation to attach the saved strategy to an account and run it in paper or live mode.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Strategy Templates */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base">Strategy Templates</CardTitle>
                <CardDescription>Pick one to pre-fill the create form, then save it as your own strategy.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-3">
                  {strategyTemplates.map((template) => (
                    <Card 
                      key={template.id} 
                      className="bg-zinc-800 border-zinc-700 cursor-pointer hover:border-blue-500 transition-colors"
                      onClick={() => handleUseTemplate(template)}
                    >
                      <CardContent className="p-4">
                        <h4 className="font-semibold text-sm mb-1">{template.name}</h4>
                        <p className="text-xs text-zinc-400 mb-2">{template.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {template.indicators.slice(0, 3).map((ind, idx) => (
                            <Badge key={idx} variant="outline" className="text-[9px]">
                              {typeof ind === 'object' ? ind.type : ind}
                            </Badge>
                          ))}
                        </div>
                        <div className="mt-3 text-xs font-medium text-blue-400">Use Template</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Strategy Catalog */}
            {catalogStrategies.length > 0 && (
              <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base">Strategy Catalog</CardTitle>
                <CardDescription>Clone one of these platform strategies into your library, then link it in Automation.</CardDescription>
              </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    {catalogStrategies.map((cat) => (
                      <Card
                        key={cat.id}
                        className="bg-zinc-800 border-zinc-700 cursor-pointer hover:border-blue-500 transition-colors"
                        onClick={() => handleCloneCatalogStrategy(cat.id)}
                      >
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-sm mb-1">{cat.name}</h4>
                          <p className="text-xs text-zinc-400 mb-2 line-clamp-2">{cat.description}</p>
                          <div className="flex gap-1 flex-wrap">
                            {cat.min_plan && cat.min_plan !== "FREE" && (
                              <Badge variant="outline" className="text-[9px] text-yellow-400 border-yellow-400/40">
                                {cat.min_plan}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[9px]">
                              {cat.language || "python"}
                            </Badge>
                          </div>
                          <div className="mt-3 text-xs font-medium text-blue-400">Clone To My Library</div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Add Strategy Modal */}
            {showAddStrategy && (
              <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4">
                <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg border border-zinc-800 bg-zinc-900 p-6 shadow-xl">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white">Create Strategy</h3>
                      <p className="mt-1 text-sm text-zinc-400">
                        Save the strategy to your library first. Then open <span className="text-white">Automation</span> to attach it to an account.
                      </p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={closeStrategyModal} aria-label="Close create strategy modal">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="mt-6 space-y-4 overflow-y-auto pr-1">
                    <div className="space-y-2">
                      <Label htmlFor="strategy-mode">Strategy Mode</Label>
                      <select
                        id="strategy-mode"
                        value={newStrategy.mode}
                        onChange={(e) => setNewStrategy((current) => ({
                          ...current,
                          mode: e.target.value,
                          language: e.target.value === "custom" ? current.language : "pinescript",
                        }))}
                        className="flex h-9 w-full items-center rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white"
                      >
                        <option value="visual">Visual Builder</option>
                        <option value="custom">Custom Script</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="strategy-name">Strategy Name</Label>
                      <Input
                        id="strategy-name"
                        placeholder="My Strategy"
                        value={newStrategy.name}
                        onChange={(e) => setNewStrategy({ ...newStrategy, name: e.target.value })}
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="strategy-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="strategy-description">Description</Label>
                      <Input
                        id="strategy-description"
                        placeholder="Strategy description..."
                        value={newStrategy.description}
                        onChange={(e) => setNewStrategy({ ...newStrategy, description: e.target.value })}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="strategy-engine">Execution Engine</Label>
                      <select
                        id="strategy-engine"
                        value={newStrategy.strategy_type}
                        onChange={(e) => updateStrategyEngine(e.target.value)}
                        className="flex h-9 w-full items-center rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white"
                      >
                        {STRATEGY_ENGINES.map((engine) => (
                          <option key={engine.value} value={engine.value}>
                            {engine.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-zinc-400">
                        The create step uses stable defaults for this engine. You can refine behavior later in backtests and automation.
                      </p>
                    </div>
                    <div className="space-y-3">
                      <Label>Strategy Inputs</Label>
                      <StrategyRuntimeFields
                        engine={newStrategy.strategy_type}
                        params={newStrategy.runtime_params}
                        onChange={updateStrategyRuntimeParam}
                        gridClassName="grid grid-cols-1 gap-3 sm:grid-cols-2"
                        labelClassName="text-sm font-medium text-zinc-300"
                        inputClassName="border-zinc-700 bg-zinc-800 text-sm text-white"
                        switchCardClassName="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3"
                        hintClassName="text-xs text-zinc-400"
                      />
                    </div>
                    {newStrategy.mode === "custom" && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="strategy-language">Language</Label>
                          <select
                            id="strategy-language"
                            value={newStrategy.language}
                            onChange={(e) => setNewStrategy({ ...newStrategy, language: e.target.value })}
                            className="flex h-9 w-full items-center rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-white"
                          >
                            <option value="pinescript">Pine Script</option>
                            <option value="python">Python</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="strategy-code">Code</Label>
                          <CodeEditor
                            language={newStrategy.language || "python"}
                            value={newStrategy.code}
                            onChange={(val) => setNewStrategy({ ...newStrategy, code: val })}
                            height="280px"
                            placeholder={
                              newStrategy.language === "pinescript"
                                ? '//@version=5\nstrategy("My Strategy", overlay=true)\n'
                                : "def generate_signals(bars, indicators):\n    signals = []\n    return signals\n"
                            }
                          />
                        </div>
                      </>
                    )}
                    {newStrategy.mode === "visual" && (
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold text-white">Visual Builder</Label>
                        <VisualStrategyBuilder
                          indicators={newStrategy.indicators || []}
                          entryConditions={newStrategy.entry_conditions || {}}
                          exitConditions={newStrategy.exit_conditions || {}}
                          onChange={({ indicators, entryConditions, exitConditions }) =>
                            setNewStrategy((prev) => ({
                              ...prev,
                              indicators,
                              entry_conditions: entryConditions,
                              exit_conditions: exitConditions,
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <Button variant="outline" onClick={closeStrategyModal}>Cancel</Button>
                    <Button onClick={handleAddStrategy} data-testid="save-strategy-btn">Create Strategy</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Strategies List */}
            <div className="grid gap-4">
              {allStrategies.length === 0 ? (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Zap className="w-12 h-12 text-zinc-400 mb-4" />
                    <p className="text-zinc-400 mb-4">No strategies created yet</p>
                    <p className="mb-6 max-w-md text-center text-sm text-zinc-400">
                      Start with a template or create one from scratch. After saving, open the Automation tab to link it to an account.
                    </p>
                    <Button onClick={openBlankStrategyModal}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Strategy
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                allStrategies.map((strategy) => (
                  <Card key={strategy.id} className="bg-zinc-900 border-zinc-800">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Zap className={`w-5 h-5 ${strategy.is_active ? 'text-yellow-400' : 'text-zinc-400'}`} />
                          <CardTitle className="text-lg">{strategy.name}</CardTitle>
                          <Badge variant="outline">{strategy.source === "custom" ? (strategy.language || "custom") : "visual"}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleDeleteStrategy(strategy.source === "custom" ? `custom:${strategy.id}` : strategy.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-zinc-400 mb-3">{strategy.description || "No description"}</p>
                      {strategy.source === "custom" ? (
                        <div className="text-xs text-zinc-400 font-mono line-clamp-4 whitespace-pre-wrap">
                          {strategy.code || "No code saved"}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(strategy.indicators || []).map((ind, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {typeof ind === 'object' ? `${ind.type}${ind.period ? ` (${ind.period})` : ''}` : ind}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Risk Settings Tab */}
          <TabsContent value="backtest" className="space-y-4">
            <BacktestPanel
              visualStrategies={strategies}
              catalogStrategies={catalogStrategies}
              customStrategies={customStrategies}
              onStrategiesUpdated={fetchData}
            />
          </TabsContent>

          <TabsContent value="trade-audit" className="space-y-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-bold">Trade Audit</h2>
                <p className="mt-1 text-sm text-zinc-400">
                  Search entries, exits, broker confirmations, and TradoPro Takes Control events.
                </p>
              </div>
              <Button onClick={() => loadTradeAudit()} disabled={tradeAuditLoading}>
                {tradeAuditLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Search
              </Button>
            </div>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ClipboardList className="h-4 w-4 text-blue-400" />
                  Audit Search
                </CardTitle>
                <CardDescription>
                  Filter by account, trading date/time, broker trade id, broker position id, order id, or reason text.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(220px,0.9fr)_minmax(180px,0.7fr)_minmax(180px,0.7fr)]">
                  <div className="space-y-2">
                    <Label htmlFor="trade-audit-q">Search</Label>
                    <Input
                      id="trade-audit-q"
                      value={tradeAuditFilters.q}
                      onChange={(event) => setTradeAuditFilters((current) => ({ ...current, q: event.target.value }))}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") loadTradeAudit();
                      }}
                      placeholder="Broker order id, trade id, account, reason..."
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Account</Label>
                    <Select
                      value={tradeAuditFilters.trading_account_id}
                      onValueChange={(value) => setTradeAuditFilters((current) => ({ ...current, trading_account_id: value }))}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="All accounts" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">All accounts</SelectItem>
                        {accounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="trade-audit-start">From</Label>
                    <Input
                      id="trade-audit-start"
                      type="datetime-local"
                      value={tradeAuditFilters.start}
                      onChange={(event) => setTradeAuditFilters((current) => ({ ...current, start: event.target.value }))}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="trade-audit-end">To</Label>
                    <Input
                      id="trade-audit-end"
                      type="datetime-local"
                      value={tradeAuditFilters.end}
                      onChange={(event) => setTradeAuditFilters((current) => ({ ...current, end: event.target.value }))}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => loadTradeAudit()} disabled={tradeAuditLoading}>
                    Refresh Results
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const reset = defaultAuditFilters();
                      setTradeAuditFilters(reset);
                      loadTradeAudit(reset);
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>

            {tradeAuditError && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {tradeAuditError}
              </div>
            )}

            <div className="space-y-3">
              {tradeAuditLoading && tradeAuditRows.length === 0 ? (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="flex items-center justify-center gap-2 py-10 text-zinc-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading audit records...
                  </CardContent>
                </Card>
              ) : tradeAuditRows.length === 0 ? (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="py-10 text-center text-sm text-zinc-400">
                    No trade audit records match the current filters.
                  </CardContent>
                </Card>
              ) : (
                tradeAuditRows.map((trade) => {
                  const pnl = Number(trade.pnl || 0);
                  const reasons = trade.reasons || trade.audit_summary || {};
                  return (
                    <Card key={trade.id} className="bg-zinc-900 border-zinc-800">
                      <CardContent className="space-y-4 p-4">
                        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline">{trade.symbol}</Badge>
                              <Badge variant={String(trade.side).toUpperCase() === "BUY" ? "default" : "destructive"}>
                                {trade.side} {trade.quantity}
                              </Badge>
                              <Badge variant="secondary">{trade.status}</Badge>
                              <Badge variant="outline">{trade.execution_source}</Badge>
                            </div>
                            <div className="text-sm font-medium text-zinc-100">{trade.account_name || "Unknown account"}</div>
                            <div className="text-xs text-zinc-400">
                              Entry {formatDateTimeDisplay(trade.entry_time || trade.created_at, appTimeZone)}
                              {trade.exit_time ? ` - Exit ${formatDateTimeDisplay(trade.exit_time, appTimeZone)}` : ""}
                            </div>
                          </div>
                          <div className="grid gap-2 text-xs text-zinc-400 sm:grid-cols-3 xl:min-w-[520px]">
                            <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-2">
                              <div className="uppercase tracking-[0.14em] text-zinc-500">Broker Order</div>
                              <div className="font-mono text-zinc-200">{compactId(trade.broker_order_id)}</div>
                            </div>
                            <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-2">
                              <div className="uppercase tracking-[0.14em] text-zinc-500">Broker Position</div>
                              <div className="font-mono text-zinc-200">{compactId(trade.broker_position_id)}</div>
                            </div>
                            <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-2">
                              <div className="uppercase tracking-[0.14em] text-zinc-500">P&L</div>
                              <div className={`font-mono ${pnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                                {formatAuditMoney(trade.pnl)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-3 lg:grid-cols-3">
                          <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Entry Reason</div>
                            <p className="text-sm text-zinc-200">{reasons.entry_reason || "--"}</p>
                          </div>
                          <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Exit Reason</div>
                            <p className="text-sm text-zinc-200">{reasons.exit_reason || "Still open / no exit recorded"}</p>
                          </div>
                          <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Control Reason</div>
                            <p className="text-sm text-zinc-200">{reasons.control_reason || "Native TradoPro trade"}</p>
                          </div>
                        </div>

                        {Array.isArray(trade.timeline_preview) && trade.timeline_preview.length > 0 && (
                          <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">Recent Timeline</div>
                            <div className="space-y-2">
                              {trade.timeline_preview.slice(0, 4).map((event, index) => (
                                <div key={`${trade.id}-${event.type}-${index}`} className="flex flex-col gap-1 text-xs text-zinc-400 md:flex-row md:items-center md:justify-between">
                                  <span className="font-medium text-zinc-300">{event.type}</span>
                                  <span>{formatDateTimeDisplay(event.at, appTimeZone)}</span>
                                  <span className="md:max-w-[55%] md:text-right">{event.details?.reason || event.details?.message || event.details?.mechanism_key || ""}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Risk Settings Tab */}
          <TabsContent value="risk" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Risk Management Settings</h2>
              <Button onClick={handleSaveRiskSettings} disabled={riskSaving || riskLoadState === 'loading' || riskLoadState === 'error'} data-testid="save-risk-settings-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </Button>
            </div>

            {riskLoadState === 'unsaved' && <p role="alert" className="text-amber-300">These are suggested defaults, not saved account limits. Review them and save settings before enabling entries.</p>}
            {riskLoadState === 'error' && <p role="alert" className="text-red-300">Risk settings could not be loaded. Reload this page before editing; existing limits have not been replaced.</p>}
            {accounts.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Shield className="w-12 h-12 text-zinc-400 mb-4" />
                  <p className="text-zinc-400 mb-2">No trading accounts available</p>
                  <p className="text-sm text-zinc-400 text-center max-w-md">
                    Connect a broker account before configuring account-level risk controls.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <Card className="sticky top-0 z-10 border-purple-500/40 bg-zinc-900/95 backdrop-blur supports-[backdrop-filter]:bg-zinc-900/80">
                  <CardHeader className="pb-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-purple-300/80">
                          Editing risk settings for
                        </div>
                        <div className="mt-0.5 truncate text-lg font-bold text-white" data-testid="risk-active-account">
                          {(accounts.find((a) => a.id === selectedRiskAccountId)?.name) || "Select an account"}
                          {accounts.find((a) => a.id === selectedRiskAccountId)?.can_trade === false ? (
                            <span className="ml-2 align-middle text-xs font-medium text-amber-300">Ineligible</span>
                          ) : null}
                        </div>
                        <div className="text-xs text-zinc-400">
                          Linked to the account selected on the Dashboard. Changing it here updates the Dashboard too.
                        </div>
                      </div>
                      <div className="w-full sm:w-72">
                        <Select value={selectedRiskAccountId} onValueChange={handleRiskAccountChange}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="risk-account-select">
                            <SelectValue placeholder="Choose account..." />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {accounts.map((account) => (
                              <SelectItem key={account.id} value={account.id}>
                                {account.name} ({account.broker_name}){account.can_trade === false ? " - Ineligible" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-xs text-zinc-400">
                      Risk settings are stored per trading account. Strategy entries fire on the linked signal timeframe;
                      protective stop and profit mechanisms can use their own timeframes below.
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-400" />
                      Protection Stack
                    </CardTitle>
                    <CardDescription>
                      Choose which account-level trade-management protections are active. Strategy signals only create entries;
                      these mechanisms manage broker-side protection and exits on their selected timeframes.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-zinc-200">Entry Gates</p>
                          <Badge variant="secondary" className="text-[10px] uppercase">Always On</Badge>
                        </div>
                        <p className="mt-2 text-xs text-zinc-400">
                          Drawdown, trade count, and max size block new entries before an order is queued.
                        </p>
                      </div>
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-zinc-200">Initial Broker Stop</p>
                          <Badge variant={riskSettings.stop_loss_enabled ? "default" : "secondary"} className="text-[10px] uppercase">
                            {riskSettings.stop_loss_enabled ? "Active" : "Off"}
                          </Badge>
                        </div>
                        <p className="mt-2 text-xs text-zinc-400">
                          Standalone broker STOP after entry confirmation, with internal fallback if broker protection is missing.
                        </p>
                      </div>
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-zinc-200">Fixed Profit Target</p>
                          <Badge
                            variant={riskSettings.enable_structure_profit_stop ? "secondary" : riskSettings.take_profit_enabled ? "default" : "secondary"}
                            className="text-[10px] uppercase"
                          >
                            {riskSettings.enable_structure_profit_stop ? "Paused" : riskSettings.take_profit_enabled ? "Active" : "Off"}
                          </Badge>
                        </div>
                        <p className="mt-2 text-xs text-zinc-400">
                          Optional fixed exit. Paused automatically when Market Structure Trailing Stop is active.
                        </p>
                      </div>
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-zinc-200">Structure Trail</p>
                          <Badge variant={riskSettings.enable_structure_profit_stop ? "default" : "secondary"} className="text-[10px] uppercase">
                            {riskSettings.enable_structure_profit_stop ? "Active" : "Off"}
                          </Badge>
                        </div>
                        <p className="mt-2 text-xs text-zinc-400">
                          Trails behind confirmed support/resistance and modifies the live broker stop when confirmed.
                        </p>
                      </div>
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-zinc-200">3-Bar Exit</p>
                          <Badge variant={riskSettings.enable_three_bar_reversal_exit ? "default" : "secondary"} className="text-[10px] uppercase">
                            {riskSettings.enable_three_bar_reversal_exit ? "Active" : "Off"}
                          </Badge>
                        </div>
                        <p className="mt-2 text-xs text-zinc-400">
                          Optional profit-protection exit when a confirmed reversal forms against the open position.
                        </p>
                      </div>
                      <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-zinc-200">Emergency Loss</p>
                          <Badge variant={riskSettings.enable_max_position_loss ? "default" : "secondary"} className="text-[10px] uppercase">
                            {riskSettings.enable_max_position_loss ? "Active" : "Off"}
                          </Badge>
                        </div>
                        <p className="mt-2 text-xs text-zinc-400">
                          Highest-priority account-level loss circuit breaker for already-open positions.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 rounded-md border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-xs text-zinc-400">
                      Priority: broker truth and working-order checks → entry gates → emergency max-loss and hard stops →
                      initial broker stop → structure trailing stop → profit-protection exits. When one rule wants to close and
                      another wants to move a stop, the close recommendation wins.
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Stop Loss & Take Profit */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-red-400" />
                    Initial Broker Stop
                  </CardTitle>
                  <CardDescription>
                    Places a standalone broker-side STOP after the entry is confirmed. This is the first live protection layer.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                    <div>
                      <Label htmlFor="stop_loss_enabled">Enable initial protective stop</Label>
                      <p className="text-xs text-zinc-400 mt-1">Required if you want TradoPro to create broker-visible protection after entry.</p>
                    </div>
                    <Switch
                      id="stop_loss_enabled"
                      checked={Boolean(riskSettings.stop_loss_enabled)}
                      onCheckedChange={(checked) => setRiskSettings({ ...riskSettings, stop_loss_enabled: checked })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stop Loss Type</Label>
                    <Select
                      value={riskSettings.stop_loss_type}
                      onValueChange={(v) => setRiskSettings({ ...riskSettings, stop_loss_type: v })}
                      disabled={!riskSettings.stop_loss_enabled}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TICKS">Ticks</SelectItem>
                        <SelectItem value="PERCENT">Percentage</SelectItem>
                        <SelectItem value="VALUE">Dollar Value</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
	                  <div className="space-y-2">
	                    <Label>Stop Loss Value</Label>
	                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={riskSettings.stop_loss_value}
                        onChange={(e) => setRiskSettings({ ...riskSettings, stop_loss_value: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="stop-loss-value-input"
                        disabled={!riskSettings.stop_loss_enabled}
                      />
                      <span className="text-sm text-zinc-400">
                        {riskSettings.stop_loss_type === 'TICKS' ? 'ticks' : 
                         riskSettings.stop_loss_type === 'PERCENT' ? '%' : '$'}
	                      </span>
	                    </div>
	                  </div>
	                  <div className="rounded-md border border-purple-500/20 bg-purple-500/10 px-3 py-3 space-y-3">
	                    <div className="flex items-start justify-between gap-3">
	                      <div>
	                        <Label htmlFor="enable_structure_initial_stop">Use latest support/resistance for initial stop</Label>
	                        <p className="text-xs text-zinc-300 mt-1">
	                          When a broker position opens, place the first broker STOP at the latest structure zone plus the buffer,
	                          but only when it protects better than the fixed stop above.
	                        </p>
	                      </div>
	                      <Switch
	                        id="enable_structure_initial_stop"
	                        checked={Boolean(riskSettings.enable_structure_initial_stop)}
	                        onCheckedChange={(checked) => setRiskSettings({ ...riskSettings, enable_structure_initial_stop: checked })}
	                        disabled={!riskSettings.stop_loss_enabled}
	                      />
	                    </div>
	                    {riskSettings.enable_structure_initial_stop && (
	                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
	                        <div className="space-y-2">
	                          <Label>Initial Structure Timeframe</Label>
	                          <Select
	                            value={String(riskSettings.structure_initial_stop_timeframe_minutes || 2)}
	                            onValueChange={(v) => setRiskSettings({ ...riskSettings, structure_initial_stop_timeframe_minutes: Number(v) })}
	                          >
	                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
	                              <SelectValue />
	                            </SelectTrigger>
	                            <SelectContent>
	                              {TRADE_MANAGEMENT_TIMEFRAMES.map((option) => (
	                                <SelectItem key={option.value} value={String(option.value)}>
	                                  {option.label}
	                                </SelectItem>
	                              ))}
	                            </SelectContent>
	                          </Select>
	                        </div>
	                        <div className="space-y-2">
	                          <Label>Structure Buffer (ticks)</Label>
	                          <Input
	                            type="number"
	                            min={0}
	                            value={riskSettings.structure_stop_buffer_ticks}
	                            onChange={(e) => setRiskSettings({ ...riskSettings, structure_stop_buffer_ticks: parseInt(e.target.value) || 0 })}
	                            className="bg-zinc-800 border-zinc-700"
	                          />
	                        </div>
	                      </div>
	                    )}
	                  </div>
	                </CardContent>
	              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    Fixed Profit Target
                  </CardTitle>
                  <CardDescription>
                    Optional fixed target. It is automatically paused when Market Structure Trailing Stop is enabled.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-950/50 px-3 py-2">
                    <div>
                      <Label htmlFor="take_profit_enabled">Enable fixed take profit</Label>
                      <p className="text-xs text-zinc-400 mt-1">
                        {riskSettings.enable_structure_profit_stop
                          ? "Saved, but not used while structure trailing is active."
                          : "Creates a fixed profit exit when structure trailing is off."}
                      </p>
                    </div>
                    <Switch
                      id="take_profit_enabled"
                      checked={Boolean(riskSettings.take_profit_enabled)}
                      onCheckedChange={(checked) => setRiskSettings({ ...riskSettings, take_profit_enabled: checked })}
                    />
                  </div>
                  {riskSettings.enable_structure_profit_stop && (
                    <div className="rounded-md border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-xs text-yellow-200">
                      Fixed take profit is paused because Market Structure Trailing Stop is managing profit with a broker stop.
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Take Profit Type</Label>
                    <Select
                      value={riskSettings.take_profit_type}
                      onValueChange={(v) => setRiskSettings({ ...riskSettings, take_profit_type: v })}
                      disabled={!riskSettings.take_profit_enabled || riskSettings.enable_structure_profit_stop}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TICKS">Ticks</SelectItem>
                        <SelectItem value="PERCENT">Percentage</SelectItem>
                        <SelectItem value="VALUE">Dollar Value</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
	                  <div className="space-y-2">
	                    <Label>Take Profit Value</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={riskSettings.take_profit_value}
                        onChange={(e) => setRiskSettings({ ...riskSettings, take_profit_value: parseFloat(e.target.value) })}
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="take-profit-value-input"
                        disabled={!riskSettings.take_profit_enabled || riskSettings.enable_structure_profit_stop}
                      />
                      <span className="text-sm text-zinc-400">
                        {riskSettings.take_profit_type === 'TICKS' ? 'ticks' : 
                         riskSettings.take_profit_type === 'PERCENT' ? '%' : '$'}
                      </span>
	                    </div>
	                  </div>
		                  <div className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-3 space-y-3">
		                    <div className="flex items-start justify-between gap-3">
		                      <div>
		                        <Label htmlFor="enable_three_bar_reversal_exit">Three Bar Reversal Exit</Label>
		                        <p className="text-xs text-zinc-300 mt-1">
		                          Exit an open trade when a confirmed 3-bar reversal forms against the current position.
		                        </p>
		                      </div>
		                      <Switch
		                        id="enable_three_bar_reversal_exit"
		                        checked={Boolean(riskSettings.enable_three_bar_reversal_exit)}
		                        onCheckedChange={(checked) => setRiskSettings({ ...riskSettings, enable_three_bar_reversal_exit: checked })}
		                      />
		                    </div>
		                    {riskSettings.enable_three_bar_reversal_exit && (
		                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
		                        <div className="space-y-2">
		                          <Label>Detection Timeframe</Label>
		                          <Select
		                            value={String(riskSettings.three_bar_reversal_exit_timeframe || 5)}
		                            onValueChange={(v) => setRiskSettings({ ...riskSettings, three_bar_reversal_exit_timeframe: Number(v) })}
		                          >
		                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
		                              <SelectValue />
		                            </SelectTrigger>
		                            <SelectContent>
		                              {TRADE_MANAGEMENT_TIMEFRAMES.map((option) => (
		                                <SelectItem key={option.value} value={String(option.value)}>
		                                  {option.label}
		                                </SelectItem>
		                              ))}
		                            </SelectContent>
		                          </Select>
		                        </div>
		                        <div className="space-y-2">
		                          <Label>Pattern Mode</Label>
		                          <Select
		                            value={riskSettings.three_bar_reversal_exit_mode || "enhanced_only"}
		                            onValueChange={(v) => setRiskSettings({ ...riskSettings, three_bar_reversal_exit_mode: v })}
		                          >
		                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
		                              <SelectValue />
		                            </SelectTrigger>
		                            <SelectContent>
		                              <SelectItem value="enhanced_only">Enhanced only</SelectItem>
		                              <SelectItem value="normal">Normal</SelectItem>
		                            </SelectContent>
		                          </Select>
		                        </div>
		                        <div className="space-y-2">
		                          <Label>Action</Label>
		                          <Select
		                            value={riskSettings.three_bar_reversal_exit_action || "close_position"}
		                            onValueChange={(v) => setRiskSettings({ ...riskSettings, three_bar_reversal_exit_action: v })}
		                          >
		                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
		                              <SelectValue />
		                            </SelectTrigger>
		                            <SelectContent>
		                              <SelectItem value="close_position">Close Position</SelectItem>
		                              <SelectItem value="alert_only">Alert Only</SelectItem>
		                            </SelectContent>
		                          </Select>
		                        </div>
		                        <div className="space-y-2">
		                          <Label>Minimum Unrealized Profit ($)</Label>
		                          <Input
		                            type="number"
		                            min={0}
		                            placeholder="Optional"
		                            value={riskSettings.three_bar_reversal_exit_min_profit_value}
		                            onChange={(e) => setRiskSettings({ ...riskSettings, three_bar_reversal_exit_min_profit_value: e.target.value })}
		                            disabled={normalizeThreeBarReversalPnlMode(riskSettings.three_bar_reversal_exit_pnl_mode) === "loss_only"}
		                            className="bg-zinc-800 border-zinc-700"
		                          />
		                          <p className="text-xs text-zinc-500">Applies only to profitable exits.</p>
		                        </div>
		                        <div className="space-y-2 sm:col-span-2">
		                          <Label>P&L Activation</Label>
		                          <Select
		                            value={normalizeThreeBarReversalPnlMode(riskSettings.three_bar_reversal_exit_pnl_mode)}
		                            onValueChange={(v) => setRiskSettings({
		                              ...riskSettings,
		                              three_bar_reversal_exit_pnl_mode: v,
		                              three_bar_reversal_exit_profit_only: v === "profit_only",
		                            })}
		                          >
		                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
		                              <SelectValue />
		                            </SelectTrigger>
		                            <SelectContent>
		                              <SelectItem value="profit_only">Profit only</SelectItem>
		                              <SelectItem value="loss_only">Loss only</SelectItem>
		                              <SelectItem value="both">Profit or loss</SelectItem>
		                            </SelectContent>
		                          </Select>
		                          <p className="text-xs text-zinc-400">
		                            Choose whether a confirmed reversal can close only winners, only losing positions, or either.
		                          </p>
		                        </div>
		                        <div className="flex items-start gap-3 sm:col-span-2">
		                          <Switch
		                            id="three_bar_reversal_exit_confirm_on_closed_bar"
		                            checked={Boolean(riskSettings.three_bar_reversal_exit_confirm_on_closed_bar)}
		                            onCheckedChange={(checked) => setRiskSettings({ ...riskSettings, three_bar_reversal_exit_confirm_on_closed_bar: checked })}
		                          />
		                          <div>
		                            <Label htmlFor="three_bar_reversal_exit_confirm_on_closed_bar">Confirm on closed bar</Label>
		                            <p className="text-xs text-zinc-400 mt-1">Use completed candles for the pattern confirmation.</p>
		                          </div>
		                        </div>
		                      </div>
		                    )}
		                  </div>
	                </CardContent>
	              </Card>

              {/* Daily Limits */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    Entry Gates
                  </CardTitle>
                  <CardDescription>
                    These limits block new entries. They do not close an already-open broker position.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Max Daily Drawdown ($)</Label>
                    <Input
                      type="number"
                      value={riskSettings.max_daily_drawdown}
                      onChange={(e) => setRiskSettings({ ...riskSettings, max_daily_drawdown: parseFloat(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="max-daily-drawdown-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Daily Trades</Label>
                    <Input
                      type="number"
                      value={riskSettings.max_daily_trades}
                      onChange={(e) => setRiskSettings({ ...riskSettings, max_daily_trades: parseInt(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Position Size</Label>
                    <Input
                      type="number"
                      value={riskSettings.max_position_size}
                      onChange={(e) => setRiskSettings({ ...riskSettings, max_position_size: parseInt(e.target.value) })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Trailing Stop & Circuit Breakers */}
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-400" />
                    Broker Execution Rules
                  </CardTitle>
                  <CardDescription>
                    TopstepX/ProjectX entries use separate broker orders. Attached brackets are not relied on.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Topstep Bracket Mode</Label>
                    <Select
                      value={riskSettings.topstep_bracket_mode}
                      onValueChange={(v) => setRiskSettings({ ...riskSettings, topstep_bracket_mode: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unknown">Unknown</SelectItem>
                        <SelectItem value="none">No Broker Brackets</SelectItem>
                        <SelectItem value="position_brackets">Position Brackets</SelectItem>
                        <SelectItem value="auto_oco">Auto OCO Brackets</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-400">
                      Attached brackets are currently disabled for TopstepX/ProjectX. TradoPro uses a separate broker STOP after
                      position confirmation and modifies that STOP when Market Structure Trailing Stop ratchets.
                    </p>
                  </div>
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-4 text-sm text-zinc-400">
                    Before any new entry, TradoPro should see the account flat with no working stop, stop-limit, limit, or orphan orders.
                    If a broker working order remains, entry is blocked until the broker confirms cancellation or the user clears it.
                  </div>
                </CardContent>
              </Card>

              {/* Maximum Position Loss */}
              <Card className="bg-zinc-900 border-zinc-800 col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-400" />
                    Maximum Position Loss
                  </CardTitle>
                  <CardDescription>
                    Emergency loss circuit breaker for every open position on this account, regardless of strategy.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-md border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-xs text-zinc-400">
                    Runs before structure trailing and fixed targets. Live closes are submitted through the execution queue;
                    move-stop actions tighten the protection level and must never loosen an existing stop.
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="enable_max_position_loss"
                      checked={riskSettings.enable_max_position_loss}
                      onChange={(e) => setRiskSettings({ ...riskSettings, enable_max_position_loss: e.target.checked })}
                      className="w-4 h-4 accent-orange-500"
                    />
                    <Label htmlFor="enable_max_position_loss">Enable Maximum Position Loss</Label>
                  </div>
                  {riskSettings.enable_max_position_loss && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Loss Type</Label>
                          <Select
                            value={riskSettings.max_position_loss_type}
                            onValueChange={(v) => setRiskSettings({ ...riskSettings, max_position_loss_type: v })}
                          >
                            <SelectTrigger className="bg-zinc-800 border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="currency">Dollar ($)</SelectItem>
                              <SelectItem value="points">Points</SelectItem>
                              <SelectItem value="ticks">Ticks</SelectItem>
                              <SelectItem value="percent">Percent (%)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>
                            Max Loss&nbsp;
                            <span className="text-zinc-400">
                              ({riskSettings.max_position_loss_type === "currency" ? "$"
                                : riskSettings.max_position_loss_type === "percent" ? "%"
                                : riskSettings.max_position_loss_type})
                            </span>
                          </Label>
                          <Input
                            type="number"
                            min={0}
                            value={riskSettings.max_position_loss_value}
                            onChange={(e) => setRiskSettings({ ...riskSettings, max_position_loss_value: parseFloat(e.target.value) })}
                            className="bg-zinc-800 border-zinc-700"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Action When Triggered</Label>
                        <Select
                          value={riskSettings.max_position_loss_action}
                          onValueChange={(v) => setRiskSettings({ ...riskSettings, max_position_loss_action: v })}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="close_position">Close Position</SelectItem>
                            <SelectItem value="move_stop">Move Stop to Max Loss Level</SelectItem>
                            <SelectItem value="alert_only">Alert Only (no action)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-zinc-400">
                          {riskSettings.max_position_loss_action === "move_stop"
                            ? "Stop is moved to the exact price where loss equals the threshold. Only tightens — never loosens."
                            : riskSettings.max_position_loss_action === "alert_only"
                            ? "Logs a warning. No order or stop modification is made."
                            : "Position is closed (paper: immediately; live: queued for broker execution)."}
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Market Structure Trailing Stop */}
              <Card className="bg-zinc-900 border-zinc-800 col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    Market Structure Trailing Stop
                  </CardTitle>
                  <CardDescription>
                    Trails the stop behind confirmed market structure, such as support/resistance or swing zones.
                    The stop only moves when a new valid structure level forms, never simply because price moved.
                    For long trades, it follows higher support. For short trades, it follows lower resistance.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-md border border-zinc-800 bg-zinc-950/50 px-3 py-2 text-xs text-zinc-400">
                    For live TopstepX/ProjectX positions, TradoPro keeps a broker-visible standalone STOP as the primary protection.
                    When a confirmed structure level improves the stop, TradoPro modifies or replaces the broker STOP first, then mirrors
                    the confirmed price in the dashboard. The internal stop remains an emergency fallback when broker protection is missing.
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="enable_structure_profit_stop"
                      checked={riskSettings.enable_structure_profit_stop}
                      onChange={(e) => setRiskSettings({ ...riskSettings, enable_structure_profit_stop: e.target.checked })}
                      className="w-4 h-4 accent-purple-500"
                    />
                    <Label htmlFor="enable_structure_profit_stop">Enable Market Structure Trailing Stop</Label>
                  </div>
                  {riskSettings.enable_structure_profit_stop && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Trailing Stop Timeframe</Label>
                        <Select
                          value={String(riskSettings.structure_stop_timeframe_minutes || 5)}
                          onValueChange={(v) => setRiskSettings({ ...riskSettings, structure_stop_timeframe_minutes: Number(v) })}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TRADE_MANAGEMENT_TIMEFRAMES.map((option) => (
                              <SelectItem key={option.value} value={String(option.value)}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-zinc-400">Candle timeframe used to trail behind swing structure after the profit gate.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Lookback Bars</Label>
                        <Input
                          type="number"
                          min={5}
                          value={riskSettings.structure_stop_lookback_bars}
                          onChange={(e) => setRiskSettings({ ...riskSettings, structure_stop_lookback_bars: parseInt(e.target.value) })}
                          className="bg-zinc-800 border-zinc-700"
                        />
                        <p className="text-xs text-zinc-400">How many recent bars to scan for confirmed swing pivots.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Buffer (ticks below/above zone)</Label>
                        <Input
                          type="number"
                          min={0}
                          value={riskSettings.structure_stop_buffer_ticks}
                          onChange={(e) => setRiskSettings({ ...riskSettings, structure_stop_buffer_ticks: parseInt(e.target.value) })}
                          className="bg-zinc-800 border-zinc-700"
                        />
                        <p className="text-xs text-zinc-400">Stop is placed this many ticks below (long) or above (short) the detected zone.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Zone Detection Method</Label>
                        <Select
                          value={riskSettings.structure_zone_detection_method}
                          onValueChange={(v) => setRiskSettings({ ...riskSettings, structure_zone_detection_method: v })}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="swing_high_low">Swing High / Low Pivots</SelectItem>
                            <SelectItem value="pivot_zone">Pivot Zone (avg of pivots)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Minimum Profit Gate Type</Label>
                        <Select
                          value={riskSettings.structure_stop_min_profit_type}
                          onValueChange={(v) => setRiskSettings({ ...riskSettings, structure_stop_min_profit_type: v })}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="points">Points</SelectItem>
                            <SelectItem value="currency">Dollar ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>
                          Minimum Profit to Activate&nbsp;
                          <span className="text-zinc-400">({riskSettings.structure_stop_min_profit_type === "currency" ? "$" : "pts"})</span>
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          value={riskSettings.structure_stop_min_profit_value}
                          onChange={(e) => setRiskSettings({ ...riskSettings, structure_stop_min_profit_value: parseFloat(e.target.value) })}
                          className="bg-zinc-800 border-zinc-700"
                        />
                        <p className="text-xs text-zinc-400">Market structure trailing only activates once position profit exceeds this value.</p>
                      </div>
                      <div className="flex items-start gap-3 col-span-2">
                        <input
                          type="checkbox"
                          id="structure_stop_profit_only"
                          checked={riskSettings.structure_stop_profit_only}
                          onChange={(e) => setRiskSettings({ ...riskSettings, structure_stop_profit_only: e.target.checked })}
                          className="mt-1 w-4 h-4 accent-purple-500"
                        />
                        <div>
                          <Label htmlFor="structure_stop_profit_only">Profit-only (stop must be beyond breakeven)</Label>
                          <p className="text-xs text-zinc-400 mt-1">
                            When enabled, the stop is only placed if it locks in profit (beyond entry). Prevents the structure stop from adding risk.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    Profit Lock / Breakeven Stop
                    <Badge variant={riskSettings.enable_profit_lock_stop ? "default" : "secondary"} className="text-[10px] uppercase">
                      {riskSettings.enable_profit_lock_stop ? "Active" : "Off"}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Once unrealized profit reaches the trigger, move the broker protective stop to entry &plusmn; offset
                    (long: entry + offset, short: entry &minus; offset) so a winner cannot become a loser. It never closes the
                    position and never loosens an existing stop; the structure trailing stop can still ratchet further.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="enable_profit_lock_stop"
                      checked={riskSettings.enable_profit_lock_stop}
                      onChange={(e) => setRiskSettings({ ...riskSettings, enable_profit_lock_stop: e.target.checked })}
                      className="w-4 h-4 accent-purple-500"
                    />
                    <Label htmlFor="enable_profit_lock_stop">Enable Profit Lock / Breakeven Stop</Label>
                  </div>
                  {riskSettings.enable_profit_lock_stop && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Trigger Type</Label>
                        <Select
                          value={riskSettings.profit_lock_trigger_type}
                          onValueChange={(v) => setRiskSettings({ ...riskSettings, profit_lock_trigger_type: v })}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="points">Points</SelectItem>
                            <SelectItem value="currency">Dollar ($)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-zinc-400">Measure the activation threshold in points or account currency.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>
                          Trigger — profit to arm&nbsp;
                          <span className="text-zinc-400">({riskSettings.profit_lock_trigger_type === "currency" ? "$" : "pts"})</span>
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          step="0.25"
                          value={riskSettings.profit_lock_trigger_value}
                          onChange={(e) => setRiskSettings({ ...riskSettings, profit_lock_trigger_value: parseFloat(e.target.value) })}
                          className="bg-zinc-800 border-zinc-700"
                        />
                        <p className="text-xs text-zinc-400">The lock arms only once unrealized profit reaches this value.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Offset from entry (pts)</Label>
                        <Input
                          type="number"
                          min={0}
                          step="0.25"
                          value={riskSettings.profit_lock_offset_points}
                          onChange={(e) => setRiskSettings({ ...riskSettings, profit_lock_offset_points: parseFloat(e.target.value) })}
                          className="bg-zinc-800 border-zinc-700"
                        />
                        <p className="text-xs text-zinc-400">
                          Stop is placed this many points beyond entry (long: above, short: below). 0 = exact breakeven;
                          a small value (e.g. 2) covers fees and avoids a single-tick stop-out at entry.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    Maximum Profit Giveback
                    <Badge variant={riskSettings.enable_profit_giveback_protection ? "default" : "secondary"} className="text-[10px] uppercase">
                      {riskSettings.enable_profit_giveback_protection ? "Active" : "Off"}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Tracks the best open profit reached by a trade. Once armed, it acts if the trade gives back more
                    than the allowed percentage of that peak profit.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="enable_profit_giveback_protection"
                      checked={riskSettings.enable_profit_giveback_protection}
                      onChange={(e) => setRiskSettings({ ...riskSettings, enable_profit_giveback_protection: e.target.checked })}
                      className="w-4 h-4 accent-purple-500"
                    />
                    <Label htmlFor="enable_profit_giveback_protection">Enable Maximum Profit Giveback</Label>
                  </div>
                  {riskSettings.enable_profit_giveback_protection && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Activation Type</Label>
                        <Select
                          value={riskSettings.profit_giveback_activation_type}
                          onValueChange={(v) => setRiskSettings({ ...riskSettings, profit_giveback_activation_type: v })}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="points">Points</SelectItem>
                            <SelectItem value="currency">Dollar ($)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-zinc-400">Measure the peak-profit activation threshold in points or account currency.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>
                          Activation Value&nbsp;
                          <span className="text-zinc-400">
                            ({riskSettings.profit_giveback_activation_type === "currency" ? "$" : "pts"})
                          </span>
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          step="0.25"
                          value={riskSettings.profit_giveback_activation_value}
                          onChange={(e) => setRiskSettings({ ...riskSettings, profit_giveback_activation_value: parseFloat(e.target.value) })}
                          className="bg-zinc-800 border-zinc-700"
                        />
                        <p className="text-xs text-zinc-400">Giveback protection arms only after peak unrealized profit reaches this value.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Maximum Giveback (%)</Label>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step="1"
                          value={riskSettings.profit_giveback_max_giveback_percent}
                          onChange={(e) => setRiskSettings({ ...riskSettings, profit_giveback_max_giveback_percent: parseFloat(e.target.value) })}
                          className="bg-zinc-800 border-zinc-700"
                        />
                        <p className="text-xs text-zinc-400">Example: 50 means action triggers after half of the peak profit has been given back.</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Action</Label>
                        <Select
                          value={riskSettings.profit_giveback_action}
                          onValueChange={(v) => setRiskSettings({ ...riskSettings, profit_giveback_action: v })}
                        >
                          <SelectTrigger className="bg-zinc-800 border-zinc-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="close_position">Close Position</SelectItem>
                            <SelectItem value="move_stop">Move Stop</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-zinc-400">Close exits the trade; move stop tightens broker protection without loosening an existing stop.</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Automation Tab */}
          <TabsContent value="automation" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Strategy Automation</h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleRunAutomation}
                  disabled={runningAutomation || accountStrategies.length === 0}
                  data-testid="run-automation-btn"
                >
                  {runningAutomation ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Run Now
                </Button>
                <Button onClick={() => setShowLinkStrategy(true)} disabled={accounts.length === 0 || strategyOptions.length === 0} data-testid="link-strategy-btn">
                  <Link className="w-4 h-4 mr-2" />
                  Link Strategy to Account
                </Button>
              </div>
            </div>

            {/* Global Auto-Trade Controls */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  Global Auto-Trade Controls
                </CardTitle>
                <CardDescription>Enable or pause all automated trading activity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-8">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium">Auto-Trading</Label>
                      <p className="text-xs text-zinc-400 mt-0.5">Execute trades automatically</p>
                    </div>
                    <Switch
                      checked={autoTradeState.enabled}
                      onCheckedChange={handleToggleAutoTrade}
                      data-testid="auto-trade-toggle"
                    />
                  </div>
                  <div className="w-px h-10 bg-zinc-700" />
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium">Paper Mode</Label>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {autoTradeState.paper_mode ? "Simulated trading (no real money)" : "Live trading — real funds at risk"}
                      </p>
                    </div>
                    <Switch
                      checked={autoTradeState.paper_mode}
                      onCheckedChange={handleTogglePaperMode}
                      data-testid="paper-mode-toggle"
                    />
                  </div>
                  <div className="ml-auto">
                    <Badge variant={autoTradeState.enabled ? "default" : "secondary"} className="text-xs">
                      {autoTradeState.enabled
                        ? (autoTradeState.paper_mode ? "Paper — Running" : "Live — Running")
                        : "Paused"}
                    </Badge>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-4 text-xs text-zinc-400">
                  <span>
                    Last scan: {autoTradeState.last_scan_at ? formatDateTimeDisplay(autoTradeState.last_scan_at, appTimeZone) : "Never"}
                  </span>
                  <span>
                    Last execution: {autoTradeState.last_execution_at ? formatDateTimeDisplay(autoTradeState.last_execution_at, appTimeZone) : "Never"}
                  </span>
                  {autoTradeState.last_error && (
                    <span className="text-red-400">Last error: {autoTradeState.last_error}</span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Signal Trigger Mode — user preference */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-400/80" />
                  <CardTitle className="text-base">Signal Trigger Mode</CardTitle>
                </div>
                <CardDescription>
                  Your preferred automation trigger style. The admin sets the platform-wide default; this lets you override it for your account.
                  <span className="block mt-1 text-zinc-400">Set to <strong>Default</strong> to inherit the admin's global setting.</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      key: 'disabled',
                      label: 'Disabled',
                      desc: 'Strategy alerts remain visible but automation does not act on them. Default for safety.',
                    },
                    {
                      key: 'bar_close',
                      label: 'Bar Close',
                      desc: 'Automation evaluates only when a candle has fully closed. No repaint risk; one decision per bar.',
                    },
                    {
                      key: 'intrabar',
                      label: 'Intrabar',
                      desc: 'Automation evaluates on every live quote (throttled). Fastest, but signals can repaint within the bar.',
                    },
                  ].map(({ key, label, desc }) => {
                    const selected = signalTriggerMode === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={savingTriggerMode}
                        onClick={() => handleSetSignalTriggerMode(key)}
                        className={`text-left rounded border px-3 py-2 transition ${
                          selected
                            ? 'border-blue-500/50 bg-blue-500/15 text-white'
                            : 'border-zinc-700 bg-zinc-800/50 text-zinc-300 hover:bg-zinc-800'
                        }`}
                      >
                        <div className="text-sm font-semibold">{label}</div>
                        <div className="mt-1 text-[11px] text-zinc-400">{desc}</div>
                      </button>
                    );
                  })}
                </div>
                {signalTriggerMode !== null && (
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      disabled={savingTriggerMode}
                      onClick={() => handleSetSignalTriggerMode(null)}
                      className="text-xs text-zinc-400 hover:text-zinc-200 underline underline-offset-2"
                    >
                      Reset to admin global default
                    </button>
                  </div>
                )}
                {signalTriggerMode === null && (
                  <p className="mt-2 text-xs text-zinc-400 italic">No preference set — using admin global default.</p>
                )}
              </CardContent>
            </Card>

            {/* Persistent Entry — user-level */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400/80" />
                  <CardTitle className="text-base">Persistent Entry (Missed-Signal Recovery)</CardTitle>
                </div>
                <CardDescription>
                  When enabled, TradoPro can re-enter after a missed signal only if the current regime is still valid, broker confirms flat, and all risk/session checks pass. Applies to all your automation links. OFF by default.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Allow Persistent Entry</Label>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {persistentEntryDraft.allow_persistent_signal_entry
                        ? "Recovery enabled — TradoPro will re-evaluate missed entries on every cycle"
                        : "Off — only fresh signal transitions trigger entries"}
                    </p>
                  </div>
                  <Switch
                    checked={persistentEntryDraft.allow_persistent_signal_entry}
                    onCheckedChange={(v) => setPersistentEntryDraft((d) => ({ ...d, allow_persistent_signal_entry: v }))}
                  />
                </div>

                {persistentEntryDraft.allow_persistent_signal_entry && (
                  <div className="space-y-3 pt-1 border-t border-zinc-800">
                    <p className="text-[11px] text-amber-200/60 leading-relaxed pt-1">
                      Guards apply per-link, per-timeframe. A 5m and 2m link track independent regimes.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Max Age (bars)</Label>
                        <p className="text-[10px] text-zinc-400">How old the regime can be before entry is blocked</p>
                        <Input type="number" min="1" max="50" className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                          value={persistentEntryDraft.persistent_entry_max_age_bars}
                          onChange={(e) => setPersistentEntryDraft((d) => ({ ...d, persistent_entry_max_age_bars: Math.max(1, parseInt(e.target.value || "6", 10) || 6) }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Min Bars in Regime</Label>
                        <p className="text-[10px] text-zinc-400">Consecutive bars confirming the regime before entry</p>
                        <Input type="number" min="1" max="20" className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                          value={persistentEntryDraft.persistent_entry_min_state_bars}
                          onChange={(e) => setPersistentEntryDraft((d) => ({ ...d, persistent_entry_min_state_bars: Math.max(1, parseInt(e.target.value || "2", 10) || 2) }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Cooldown After Close (bars)</Label>
                        <p className="text-[10px] text-zinc-400">Bars to wait after a closed trade before re-entering</p>
                        <Input type="number" min="0" max="20" className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                          value={persistentEntryDraft.persistent_entry_cooldown_after_close_bars}
                          onChange={(e) => setPersistentEntryDraft((d) => ({ ...d, persistent_entry_cooldown_after_close_bars: Math.max(0, parseInt(e.target.value || "3", 10) || 0) }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Max ATR Distance from KAMA</Label>
                        <p className="text-[10px] text-zinc-400">Blocks entry if price chased too far from structure</p>
                        <Input type="number" min="0.1" max="10" step="0.1" className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                          value={persistentEntryDraft.persistent_entry_max_atr_distance_from_kama}
                          onChange={(e) => setPersistentEntryDraft((d) => ({ ...d, persistent_entry_max_atr_distance_from_kama: Math.max(0.1, parseFloat(e.target.value || "1.5") || 1.5) }))}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs text-zinc-400">Require Broker Flat</Label>
                        <p className="text-[10px] text-zinc-400">Block entry if broker reports any open position</p>
                      </div>
                      <Switch
                        checked={persistentEntryDraft.require_broker_flat}
                        onCheckedChange={(v) => setPersistentEntryDraft((d) => ({ ...d, require_broker_flat: v }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs text-zinc-400">Require Broker State Confirmed</Label>
                        <p className="text-[10px] text-zinc-400">Block entry if broker is unreachable (fail-closed)</p>
                      </div>
                      <Switch
                        checked={persistentEntryDraft.require_broker_state_confirmed}
                        onCheckedChange={(v) => setPersistentEntryDraft((d) => ({ ...d, require_broker_state_confirmed: v }))}
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-1">
                  <Button
                    onClick={handleSavePersistentEntrySettings}
                    disabled={savingPersistentEntry}
                    size="sm"
                  >
                    {savingPersistentEntry ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : null}
                    Save Persistent Entry Settings
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Auto-Trade Account Scope</CardTitle>
                <CardDescription>
                  Auto-trading only runs on the trading accounts you enable here. Different accounts can keep different linked strategies.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {accounts.length === 0 ? (
                  <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 px-4 py-3 text-sm text-zinc-400">
                    Connect at least one broker account before enabling account-scoped automation.
                  </div>
                ) : (
                  accounts.map((account) => {
                    const linkedContracts = accountStrategies.filter((link) => link.trading_account_id === account.id).length;
                    const enabled = (autoTradeState.enabled_account_ids || []).includes(account.id);
                    return (
                      <div
                        key={account.id}
                        className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950/40 px-4 py-3"
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-100">{account.name}</div>
                          <div className="mt-1 text-xs text-zinc-400">
                            {account.broker_name} • {linkedContracts} linked contract{linkedContracts === 1 ? "" : "s"}
                            {account.can_trade === false ? " • Ineligible" : ""}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-zinc-400">Auto-Trade</span>
                              <Badge variant={enabled ? "default" : "secondary"} className="text-[10px] uppercase tracking-[0.14em]">
                                {enabled ? "On" : "Off"}
                              </Badge>
                              <Switch
                                checked={enabled}
                                onCheckedChange={(checked) => handleToggleAutomationAccount(account.id, checked)}
                                disabled={account.can_trade === false}
                                data-testid={`automation-account-toggle-${account.id}`}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-zinc-400">Takes Control</span>
                              <Switch
                                checked={Boolean(account.adopt_external_trades)}
                                onCheckedChange={(checked) => handleToggleAdoptExternalTrades(account.id, checked)}
                                disabled={account.can_trade === false}
                                data-testid={`adopt-trades-toggle-${account.id}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <p className="text-xs text-zinc-400">
                  Changing a linked strategy while that account/contract has an open position is blocked until the position is closed.
                </p>
              </CardContent>
            </Card>

            {(accounts.length === 0 || strategyOptions.length === 0) && (
              <Card className="bg-yellow-500/10 border-yellow-500/30">
                <CardContent className="flex items-center gap-3 py-4">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <p className="text-sm text-yellow-200">
                    {accounts.length === 0
                      ? "Add a trading account first before linking strategies."
                      : "Create or clone at least one strategy before linking."}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Link Strategy Dialog */}
            <Dialog open={showLinkStrategy} onOpenChange={setShowLinkStrategy}>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle>Link Strategy to Account</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Select Account</Label>
                    <Select
                      value={newLink.trading_account_id}
                      onValueChange={(v) => setNewLink({ ...newLink, trading_account_id: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Choose account..." />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.map((acc) => (
                          <SelectItem key={acc.id} value={acc.id} disabled={acc.can_trade === false}>
                            {acc.name}{acc.can_trade === false ? " (Ineligible)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Select Strategy</Label>
                    <Select
                      value={newLink.strategy_id}
                      onValueChange={(v) => setNewLink({ ...newLink, strategy_id: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue placeholder="Choose strategy..." />
                      </SelectTrigger>
                      <SelectContent>
                        {strategyOptions.map((strat) => (
                          <SelectItem key={strat.value} value={strat.value}>{strat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Contracts to Link</Label>
                    <div className="flex flex-wrap gap-2">
                      {linkInstrumentOptions.map((inst) => (
                        <Badge
                          key={inst.value}
                          variant={newLink.instruments.includes(inst.value) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleLinkInstrument(inst.value)}
                        >
                          {inst.label}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-400">
                      Each selected contract creates its own account-contract strategy link.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Contracts to Trade</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newLink.contracts_to_trade}
                      onChange={(e) => setNewLink({
                        ...newLink,
                        contracts_to_trade: Math.max(1, parseInt(e.target.value || "1", 10) || 1),
                      })}
                      className="bg-zinc-800 border-zinc-700"
                    />
                    <p className="text-xs text-zinc-400">
                      This becomes the default automation quantity for each selected account + instrument link.
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Start Active</Label>
                    <Switch
                      checked={newLink.is_active}
                      onCheckedChange={(v) => setNewLink({ ...newLink, is_active: v })}
                    />
                  </div>

                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowLinkStrategy(false)}>Cancel</Button>
                  <Button onClick={handleLinkStrategy} data-testid="confirm-link-btn">Link Strategy</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Active Automations */}
            <div>
              <h3 className="text-sm font-medium text-zinc-400 mb-3">
                Active Links ({accountStrategies.length})
              </h3>
              <div className="grid gap-3">
                {accountStrategies.length === 0 ? (
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <PlugZap className="w-12 h-12 text-zinc-400 mb-4" />
                      <p className="text-zinc-400 mb-2">No strategy links configured</p>
                      <p className="text-sm text-zinc-400 text-center max-w-md">
                        Link a strategy to a trading account to start automated trading.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  accountStrategies.map((link) => (
                    <Card key={link.id} className="bg-zinc-900 border-zinc-800">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${link.is_active ? 'bg-green-500/10' : 'bg-zinc-800'}`}>
                              {link.is_active ? (
                                <Play className="w-5 h-5 text-green-400" />
                              ) : (
                                <Pause className="w-5 h-5 text-zinc-400" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{link.account_name}</span>
                                <span className="text-zinc-400">→</span>
                                <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">
                                  {link.primary_symbol || link.instruments?.[0] || "ES"}
                                </Badge>
                                {link.broker_contract_label ? (
                                  <span className="text-xs text-zinc-400">{link.broker_contract_label}</span>
                                ) : null}
                                <span className="text-zinc-400">→</span>
                                <span className="font-semibold text-blue-400">{link.strategy_name}</span>
                              </div>
                              <div className="mt-1 text-xs text-zinc-400">
                                {(link.strategy_kind || "visual").toUpperCase()} • {(link.strategy_language || "visual").toUpperCase()} • Qty {Math.max(1, Number(link.contracts_to_trade || 1))}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={link.is_active ? "default" : "secondary"}>
                              {link.is_active ? "Running" : "Paused"}
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleLink(link.id)}
                              title={link.is_active ? "Pause automation" : "Resume automation"}
                            >
                              {link.is_active ? (
                                <Pause className="w-4 h-4" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleUnlinkStrategy(link.id)}
                            >
                              <Unlink className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {/* Per-link persistent entry override */}
                        <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs text-zinc-400 whitespace-nowrap">Persistent Entry</span>
                            {link.link_persistent_entry_enabled === null || link.link_persistent_entry_enabled === undefined ? (
                              <span className="text-[10px] text-zinc-400 bg-zinc-800 px-1.5 py-0.5 rounded">
                                Inherit global ({persistentEntryDraft.allow_persistent_signal_entry ? "ON" : "OFF"})
                              </span>
                            ) : link.link_persistent_entry_enabled ? (
                              <span className="text-[10px] text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded">Override: ON</span>
                            ) : (
                              <span className="text-[10px] text-zinc-400 bg-zinc-700/40 px-1.5 py-0.5 rounded">Override: OFF</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Button
                              variant={link.link_persistent_entry_enabled === true ? "default" : "outline"}
                              size="sm"
                              className="h-6 px-2 text-[10px]"
                              disabled={savingLinkPE === link.id}
                              onClick={() => handleSetLinkPersistentEntry(link.id, true)}
                            >
                              On
                            </Button>
                            <Button
                              variant={link.link_persistent_entry_enabled === false ? "default" : "outline"}
                              size="sm"
                              className="h-6 px-2 text-[10px]"
                              disabled={savingLinkPE === link.id}
                              onClick={() => handleSetLinkPersistentEntry(link.id, false)}
                            >
                              Off
                            </Button>
                            <Button
                              variant={link.link_persistent_entry_enabled === null || link.link_persistent_entry_enabled === undefined ? "secondary" : "ghost"}
                              size="sm"
                              className="h-6 px-2 text-[10px]"
                              disabled={savingLinkPE === link.id}
                              onClick={() => handleSetLinkPersistentEntry(link.id, null)}
                              title="Reset to global default"
                            >
                              Default
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Recent Automation Activity</h3>
              {automationRuns.length === 0 ? (
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardContent className="py-6 text-sm text-zinc-400">
                    No automation runs recorded yet.
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3">
                  {automationRuns.map((run) => (
                    <Card key={run.id} className="bg-zinc-900 border-zinc-800">
                      <CardContent className="py-3 flex items-center justify-between gap-4">
                        <div>
                          <div className="text-sm font-medium text-white">
                            {run.strategy_name || "Automation"} {run.symbol ? `• ${run.symbol}` : ""}
                          </div>
                          <div className="text-xs text-zinc-400">
                            {run.created_at ? formatDateTimeDisplay(run.created_at, appTimeZone) : "Unknown time"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="uppercase text-[10px]">{run.mode || "scan"}</Badge>
                          <Badge variant={run.status === "executed" ? "default" : run.status === "failed" || run.status === "error" ? "destructive" : "secondary"}>
                            {run.status}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-4 lg:grid-cols-[1.15fr,0.85fr]">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">Execution Health</CardTitle>
                      <CardDescription>Queue state, dead letters, and broker-sync anomalies.</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={refreshExecutionState}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReconcilePositions}
                        disabled={reconcilingPositions}
                      >
                        {reconcilingPositions ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                        Reconcile
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-400">Queued Jobs</div>
                      <div className="mt-1 text-xl font-semibold">{executionOverview.jobs?.queued || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-400">Retrying</div>
                      <div className="mt-1 text-xl font-semibold">{executionOverview.jobs?.retrying || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-400">Dead Letters</div>
                      <div className="mt-1 text-xl font-semibold text-red-400">{executionOverview.jobs?.dead_letter || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-400">Active Live Trades</div>
                      <div className="mt-1 text-xl font-semibold">{executionOverview.live_trades?.active || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-400">Stale Sync</div>
                      <div className="mt-1 text-xl font-semibold text-yellow-400">{executionOverview.live_trades?.stale_sync || 0}</div>
                    </div>
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3">
                      <div className="text-xs text-zinc-400">Anomalies</div>
                      <div className="mt-1 text-xl font-semibold text-orange-400">{executionOverview.live_trades?.anomalies || 0}</div>
                    </div>
                  </div>

                  {executionOverview.recent_dead_letters?.length > 0 && (
                    <div className="space-y-3">
                      <div className="text-sm font-medium text-zinc-300">Recent Dead Letters</div>
                      <div className="grid gap-2">
                        {executionOverview.recent_dead_letters.map((job) => (
                          <div key={job.id} className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <div className="text-sm font-medium text-white">
                                  {job.job_type} {job.trade_id ? `• ${job.trade_id.slice(0, 8)}` : ""}
                                </div>
                                <div className="text-xs text-zinc-400">
                                  {job.dead_lettered_at ? formatDateTimeDisplay(job.dead_lettered_at, appTimeZone) : "Unknown time"}
                                </div>
                                <div className="mt-1 text-xs text-red-300">{job.last_error || "No error detail"}</div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRetryExecutionJob(job.id)}
                                disabled={retryingJobId === job.id}
                              >
                                {retryingJobId === job.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                Retry
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Recent Execution Jobs</CardTitle>
                  <CardDescription>Latest broker actions and queue outcomes.</CardDescription>
                </CardHeader>
                <CardContent>
                  {executionJobs.length === 0 ? (
                    <div className="py-6 text-sm text-zinc-400">No execution jobs recorded yet.</div>
                  ) : (
                    <div className="grid gap-2">
                      {executionJobs.map((job) => (
                        <div key={job.id} className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-sm font-medium text-white">
                                {job.job_type} {job.broker_name ? `• ${job.broker_name}` : ""}
                              </div>
                              <div className="text-xs text-zinc-400">
                                {job.created_at ? formatDateTimeDisplay(job.created_at, appTimeZone) : "Unknown time"}
                              </div>
                              {job.last_error && (
                                <div className="mt-1 text-xs text-red-300">{job.last_error}</div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="uppercase text-[10px]">{job.status}</Badge>
                              {(job.status === "failed" || job.status === "dead_letter") && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRetryExecutionJob(job.id)}
                                  disabled={retryingJobId === job.id}
                                >
                                  Retry
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PlatformShell>
  );
};

export default Settings;
